<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-4 px-2">
      <div>
        <h5 class="mb-0 font-weight-bold mytask-page-title">My Tasks</h5>
        <p class="text-secondary text-sm mb-0">Tasks assigned to you, sorted by nearest due date</p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <div class="mytask-filter-tabs" role="tablist" aria-label="My task filters">
          <button
            type="button"
            class="mytask-filter-item"
            :class="{ active: activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >
            All
          </button>
          <button
            type="button"
            class="mytask-filter-item"
            :class="{ active: activeFilter === 'task' }"
            @click="activeFilter = 'task'"
          >
            <i class="fas fa-list-check me-1"></i>Task
          </button>
          <button
            type="button"
            class="mytask-filter-item"
            :class="{ active: activeFilter === 'plan' }"
            @click="activeFilter = 'plan'"
          >
            <i class="fas fa-calendar-check me-1"></i>Plan
          </button>
          <button
            type="button"
            class="mytask-filter-item"
            :class="{ active: activeFilter === 'run' }"
            @click="activeFilter = 'run'"
          >
            <i class="fas fa-play-circle me-1"></i>Test Run
          </button>
          <button
            type="button"
            class="mytask-filter-item"
            :class="{ active: activeFilter === 'jira' }"
            @click="activeFilter = 'jira'"
          >
            <i class="fab fa-jira me-1"></i>Jira
          </button>
        </div>
        <button
          type="button"
          class="btn btn-sm mb-0 bg-gradient-primary text-white mytask-add-btn"
          @click="openAddTaskModal"
        >
          <i class="fas fa-plus me-1"></i>Add Task
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
      <p class="text-secondary text-sm mt-2 mb-0">Loading your tasks...</p>
    </div>

    <!-- Load failed -->
    <div v-else-if="tasksLoadError" class="px-2 text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-danger opacity-8"></i>
      <p class="text-sm mt-3 mb-2" style="color:#344767;">{{ tasksLoadError }}</p>
      <p class="text-xs text-secondary mb-3">ตรวจสอบว่า backend รันอยู่ที่พอร์ต 3000 (<code>yarn dev</code> ที่โฟลเดอร์ BO)</p>
      <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" @click="loadTasks">
        <i class="fas fa-redo me-1"></i>Retry
      </button>
    </div>

    <!-- Stats + Task list -->
    <div v-else class="px-2">
      <!-- Stats row (hidden on Jira — counts live inside Jira card) -->
      <div v-if="activeFilter !== 'jira'" class="stats-row mb-4">
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-total">
              <i class="fas fa-tasks"></i>
            </div>
            <div>
              <p class="stat-label">Total</p>
              <h5 class="stat-value">{{ tasks.length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-plan">
              <i class="fas fa-calendar-check"></i>
            </div>
            <div>
              <p class="stat-label">Plan</p>
              <h5 class="stat-value">{{ tasks.filter(t => t.taskType === 'plan' && ['support','other'].includes(String(t.planType || '').toLowerCase())).length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-task">
              <i class="fas fa-tasks"></i>
            </div>
            <div>
              <p class="stat-label">Task</p>
              <h5 class="stat-value">{{ tasks.filter(t => t.taskType === 'plan' && String(t.planType || 'plan').toLowerCase() === 'plan').length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-run">
              <i class="fas fa-play-circle"></i>
            </div>
            <div>
              <p class="stat-label">Test Runs</p>
              <h5 class="stat-value">{{ tasks.filter(t => t.taskType === 'run').length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-jira">
              <i class="fab fa-jira"></i>
            </div>
            <div>
              <p class="stat-label">Jira</p>
              <h5 class="stat-value">{{ jiraAssignedIssues.length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-overdue">
              <i class="fas fa-clock"></i>
            </div>
            <div>
              <p class="stat-label">Overdue</p>
              <h5 class="stat-value">{{ overdueCount }}</h5>
            </div>
          </div>
        </div>
      </div>

      <!-- Jira — Plan Management style shell -->
      <div v-if="activeFilter === 'jira'" class="mytask-jira-wrap">
        <div class="card mb-3 mytask-jira-shell-card">
          <div class="card-header pb-0">
            <div class="tab-shell" role="tablist" aria-label="Jira categories">
              <div
                class="tab-item"
                :class="{ active: jiraSubTab === 'assigned' }"
                role="tab"
                :aria-selected="jiraSubTab === 'assigned'"
                @click="jiraSubTab = 'assigned'; resetJiraSprintCollapse()"
              >
                Assigned
              </div>
              <div
                class="tab-item"
                :class="{ active: jiraSubTab === 'reporter' }"
                role="tab"
                :aria-selected="jiraSubTab === 'reporter'"
                @click="jiraSubTab = 'reporter'; resetJiraSprintCollapse()"
              >
                Reporter
              </div>
              <div
                class="tab-item tab-item--future"
                :class="{ active: jiraSubTab === 'future' }"
                role="tab"
                :aria-selected="jiraSubTab === 'future'"
                @click="jiraSubTab = 'future'; resetJiraSprintCollapse()"
              >
                Assigned - Future
              </div>
            </div>
          </div>

          <div class="card-body mytask-jira-shell-content">
            <div v-if="jiraLoadingAssigned" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
              <p class="text-secondary text-sm mt-2 mb-0">Loading your Jira issues...</p>
            </div>

            <div v-else-if="jiraLoadError" class="text-center py-5">
              <i class="fas fa-exclamation-triangle fa-2x text-danger opacity-8"></i>
              <p class="text-sm mt-3 mb-2" style="color:#344767;">{{ jiraLoadError }}</p>
              <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" @click="loadJiraIssues">
                <i class="fas fa-redo me-1"></i>Retry
              </button>
            </div>

            <div v-else-if="!user?.jiraAccountId" class="text-center py-5">
              <i class="fas fa-clipboard-check fa-3x text-secondary opacity-5"></i>
              <p class="text-secondary text-sm mt-3 mb-2">Connect Jira in Profile to see assigned issues.</p>
              <router-link to="/profile" class="btn btn-sm mb-0 bg-gradient-primary text-white">Go to Profile</router-link>
            </div>

            <div v-else-if="jiraProjectGroups.length === 0" class="text-center py-5 px-3">
              <i class="fas fa-clipboard-check fa-3x text-secondary opacity-5"></i>
              <p class="text-secondary text-sm mt-3 mb-1">No issues for this tab and filter.</p>
              <p v-if="jiraConnectedMeta?.displayName || jiraConnectedMeta?.email" class="text-secondary text-xs mb-0">
                Connected as
                <strong>{{ jiraConnectedMeta.displayName || "Jira user" }}</strong>
                <span v-if="jiraConnectedMeta.email"> ({{ jiraConnectedMeta.email }})</span>
              </p>
              <p v-if="jiraAccountEmailMismatch" class="text-warning text-xs mt-2 mb-2">
                You signed in as {{ user.email }}, but Jira is linked to a different Atlassian account.
                Open Profile → Connect Jira again while logged into the Atlassian user that owns your tickets.
              </p>
              <router-link to="/profile" class="btn btn-sm mb-0 bg-gradient-primary text-white mt-2">Go to Profile</router-link>
            </div>

            <template v-else>
              <div class="mytask-jira-toolbar">
                <div class="mytask-jira-project-filter">
                  <label class="form-label text-xs text-secondary mb-1">Project</label>
                  <base-select
                    size="sm"
                    :model-value="jiraProjectFilterKeyForTab"
                    :options="jiraProjectFilterOptionsForTab"
                    option-label="label"
                    option-value="value"
                    placeholder="All"
                    @update:model-value="(v) => { jiraProjectFilterKeyForTab = v; resetJiraSprintCollapse(); }"
                  />
                </div>
                <div class="mytask-jira-toolbar-meta">
                  <p
                    v-if="jiraConnectedMeta?.displayName || jiraConnectedMeta?.email"
                    class="mytask-jira-connected text-xs text-secondary mb-0"
                  >
                    <i class="fab fa-jira me-1"></i>
                    {{ jiraConnectedMeta.displayName || "Jira user" }}
                    <span v-if="jiraConnectedMeta.email" class="d-none d-md-inline"> · {{ jiraConnectedMeta.email }}</span>
                  </p>
                  <p
                    v-if="jiraSubTab === 'assigned' || jiraSubTab === 'reporter'"
                    class="mytask-jira-tab-hint text-xs text-secondary mb-0"
                  >
                    รวม sprint ที่ <strong>active</strong> และงานที่ <strong>overdue</strong> หรือ <strong>due {{ jiraWorkWeekLabel }}</strong>
                  </p>
                  <p
                    v-else-if="jiraSubTab === 'future'"
                    class="mytask-jira-tab-hint text-xs text-secondary mb-0"
                  >
                    Sprint อนาคต (ไม่รวม overdue / due {{ jiraWorkWeekLabel }})
                  </p>
                </div>
              </div>

              <div class="stats-row mb-3 mytask-jira-stats">
                <div class="stat-col">
                  <div class="stat-card">
                    <div class="stat-icon stat-icon-total">
                      <i class="fab fa-jira"></i>
                    </div>
                    <div>
                      <p class="stat-label">Issues</p>
                      <h5 class="stat-value">{{ jiraTabIssueCount }}</h5>
                    </div>
                  </div>
                </div>
                <div class="stat-col">
                  <div class="stat-card">
                    <div class="stat-icon stat-icon-overdue">
                      <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div>
                      <p class="stat-label">Overdue</p>
                      <h5 class="stat-value">{{ jiraTabOverdueCount }}</h5>
                    </div>
                  </div>
                </div>
                <div class="stat-col">
                  <div class="stat-card">
                    <div class="stat-icon stat-icon-linked">
                      <i class="fas fa-link"></i>
                    </div>
                    <div>
                      <p class="stat-label">Linked TestOps</p>
                      <h5 class="stat-value">{{ jiraTabLinkedCount }}</h5>
                    </div>
                  </div>
                </div>
              </div>

              <div class="task-list mytask-jira-list">
                <template v-for="pg in jiraProjectGroups" :key="pg.projectKey">
                  <div class="mytask-jira-project-head">
                    <i class="fas fa-layer-group me-2 text-secondary task-project-icon"></i>
                    <span>{{ pg.projectName || pg.projectKey }}</span>
                  </div>
                  <template v-for="sg in pg.sprintGroups" :key="`${pg.projectKey}-${sg.sprintKey}`">
                    <div
                      class="mytask-jira-sprint-head"
                      role="button"
                      tabindex="0"
                      @click="toggleSprintCollapsed(pg.projectKey, sg.sprintKey)"
                      @keydown.enter="toggleSprintCollapsed(pg.projectKey, sg.sprintKey)"
                    >
                      <i
                        :class="isSprintCollapsed(pg.projectKey, sg.sprintKey) ? 'fas fa-chevron-right' : 'fas fa-chevron-down'"
                        class="me-2 mytask-jira-sprint-chevron"
                      ></i>
                      <i class="fas fa-code-branch me-2 text-secondary task-project-icon"></i>
                      <span>{{ sg.sprintName }}</span>
                      <span class="text-secondary ms-1">({{ sg.issues.length }})</span>
                      <span v-if="sg.sprintState" class="mytask-jira-sprint-state">{{ sg.sprintState }}</span>
                    </div>

                    <div v-show="!isSprintCollapsed(pg.projectKey, sg.sprintKey)" class="mytask-jira-sprint-body">
                      <div
                        v-for="issue in sg.issues"
                        :key="issue.key"
                        class="task-card"
                        :class="{ 'task-overdue': issue.dueDate && isOverdue(issue.dueDate) && !taskStatusExemptFromOverdue(issue.status) }"
                        role="button"
                        tabindex="0"
                        @click="openJiraIssueModal(issue, pg, sg)"
                        @keydown.enter="openJiraIssueModal(issue, pg, sg)"
                      >
                        <div class="task-accent" :style="jiraAccentStyle(issue)"></div>
                        <div class="task-body">
                          <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                              <span class="task-type-badge badge-jira">
                                <i class="fab fa-jira me-1"></i>{{ issue.key }}
                              </span>
                              <span
                                v-if="jiraLinkedCount(issue) > 0"
                                class="status-pill linked-testops-pill"
                                :title="jiraLinkedCardTitle(issue)"
                              >
                                <i class="fas fa-link me-1"></i>TestOps · {{ jiraLinkedCount(issue) }}
                              </span>
                              <span class="status-pill" :class="jiraIssueStatusPillClass(issue.status)">
                                {{ issue.status || "—" }}
                              </span>
                              <span
                                v-if="issue.dueDate && isOverdue(issue.dueDate) && !taskStatusExemptFromOverdue(issue.status)"
                                class="overdue-badge"
                              >
                                <i class="fas fa-exclamation-circle me-1"></i>Overdue
                              </span>
                            </div>
                            <span
                              class="task-due"
                              :class="{
                                'due-soon': issue.dueDate && isDueSoon(issue.dueDate) && !taskStatusExemptFromOverdue(issue.status),
                                'due-overdue': issue.dueDate && isOverdue(issue.dueDate) && !taskStatusExemptFromOverdue(issue.status),
                              }"
                            >
                              <i class="fas fa-calendar-alt me-1"></i>
                              {{ issue.dueDate ? formatDate(issue.dueDate) : "— No due date" }}
                            </span>
                          </div>
                          <h6 class="task-name mb-1">{{ issue.summary || "—" }}</h6>
                          <p v-if="issue.issueType" class="text-xs mb-1 mytask-jira-meta">
                            <i class="fas fa-tag me-1"></i>{{ issue.issueType }}
                          </p>
                          <p v-if="jiraSubTab === 'reporter'" class="text-xs mb-1 mytask-jira-meta">
                            <i class="fas fa-user me-1"></i>Owner: {{ issue.assignee || "Unassigned" }}
                          </p>
                          <p class="task-project mb-0">
                            <i class="fas fa-folder me-1 text-secondary task-project-icon"></i>
                            <span class="text-secondary">{{ pg.projectName || pg.projectKey }}</span>
                            <span class="text-secondary mx-1">|</span>
                            <span class="text-secondary">{{ sg.sprintName }}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredTasks.length === 0" class="text-center py-5">
        <i class="fas fa-clipboard-check fa-3x text-secondary opacity-5"></i>
        <p class="text-secondary text-sm mt-3 mb-0">No tasks for selected filter.</p>
      </div>

      <!-- Task cards (+ standalone Jira on All tab) -->
      <div v-else class="task-list">
        <div
          v-for="task in filteredTasks"
          :key="task.taskType + '-' + task.id"
          class="task-card"
          :class="{ 'task-overdue': taskCardShowOverdue(task) }"
          @click="onTaskCardClick(task)"
        >
          <!-- Left accent bar -->
          <div class="task-accent" :style="accentStyle(task)"></div>

          <!-- Main content -->
          <div class="task-body">
            <!-- Top row -->
            <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span v-if="task.taskType === 'jira'" class="task-type-badge badge-jira">
                  <i class="fab fa-jira me-1"></i>{{ task.id }}
                </span>
                <span v-else class="task-type-badge" :class="task.taskType === 'plan' ? 'badge-plan' : 'badge-run'">
                  <i :class="task.taskType === 'plan' ? planTypeIcon(task.planType) : 'fas fa-play-circle'" class="me-1"></i>
                  {{ task.taskType === 'plan' ? formatPlanType(task.planType) : 'Test Run' }}
                </span>
                <span v-if="task.isSubTask" class="task-type-badge badge-subtask">
                  <i class="fas fa-level-up-alt me-1"></i>Sub Task
                </span>
                <span class="status-pill" :class="statusPillClass(task)">{{ statusLabel(task) }}</span>
                <span v-if="taskCardShowOverdue(task)" class="overdue-badge">
                  <i class="fas fa-exclamation-circle me-1"></i>Overdue
                </span>
              </div>
              <div class="d-flex align-items-start gap-2">
                <span
                  class="task-due"
                  :class="{
                    'due-soon': taskCardShowDueSoon(task),
                    'due-overdue': taskCardShowOverdue(task),
                  }"
                >
                  <i class="fas fa-calendar-alt me-1"></i>
                  {{ task.dueDate ? formatDate(task.dueDate) : '— No due date' }}
                </span>
                <button
                  v-if="isMyTaskDeletable(task)"
                  type="button"
                  class="btn btn-link btn-sm p-0 mytask-delete-btn"
                  title="Delete"
                  @click="requestDeleteMyTask(task)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <!-- Task name -->
            <h6 class="task-name mb-1">{{ task.name }}</h6>
            <p v-if="task.taskType === 'jira' && task.issueType" class="text-xs mb-1 mytask-jira-meta">
              <i class="fas fa-tag me-1"></i>{{ task.issueType }}
            </p>
            <p v-if="task.isSubTask && task.parentTaskName" class="text-xs mb-1" style="color:#7b809a;">
              Under: {{ task.parentTaskName }}
            </p>

            <!-- Project info -->
            <p class="task-project mb-0">
              <template v-if="task.taskType === 'jira'">
                <i class="fas fa-folder me-1 text-secondary task-project-icon"></i>
                <span class="text-secondary">{{ task.projectName || task.projectKey || '—' }}</span>
                <span class="text-secondary mx-1">|</span>
                <span class="text-secondary">{{ task.sprintName || 'No Sprint' }}</span>
              </template>
              <template v-else>
                <i class="fas fa-layer-group me-1 text-secondary task-project-icon"></i>
                <span class="text-secondary">{{ task.subProjectName }}</span>
                <span class="text-secondary mx-1">|</span>
                <span class="text-secondary">{{ task.projectName }}</span>
              </template>
            </p>
            <div v-if="task.taskType !== 'jira' && task.jiraLinks && task.jiraLinks.length" class="task-jira-section mt-2" @click.stop>
              <button
                type="button"
                class="task-jira-toggle"
                :aria-expanded="isJiraExpanded(task) ? 'true' : 'false'"
                @click="toggleJiraExpand(task)"
              >
                <span class="task-jira-toggle-start">
                  <i class="fab fa-jira text-info"></i>
                  <span class="task-jira-toggle-label">Jira ({{ task.jiraLinks.length }})</span>
                </span>
                <span class="task-jira-toggle-end" aria-hidden="true">
                  <i
                    class="fas"
                    :class="isJiraExpanded(task) ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                </span>
              </button>
              <div v-if="isJiraExpanded(task)" class="task-jira-panel">
                <jira-links-panel
                  embedded
                  dense
                  :links="task.jiraLinks"
                  :key="'card-jira-' + taskCardKey(task)"
                />
              </div>
            </div>

            <!-- Test Run progress (only for runs) -->
            <div v-if="task.taskType === 'run'" class="mt-3">
              <template v-if="task.total > 0">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="text-xs text-secondary">Progress ({{ task.total }} test cases)</span>
                  <span class="text-xs font-weight-bold progress-value">{{ task.progress }}%</span>
                </div>
                <!-- Progress bar -->
                <div class="task-progress-track">
                  <div
                    class="task-progress-bar"
                    :class="{ 'task-progress-complete': task.progress === 100 }"
                    :style="{
                      height: '100%',
                      borderRadius: '3px',
                      transition: 'width 0.3s ease',
                      width: task.progress + '%'
                    }"
                  ></div>
                </div>
                <!-- Status breakdown -->
                <div class="d-flex flex-wrap gap-2">
                  <span v-if="(task.statusBreakdown?.pass || 0) > 0" class="run-stat run-stat-pass">
                    <i class="fas fa-check me-1"></i>Pass {{ task.statusBreakdown.pass }}
                  </span>
                  <span v-if="(task.statusBreakdown?.passCond || 0) > 0" class="run-stat run-stat-passcond">
                    <i class="fas fa-check-double me-1"></i>Pass* {{ task.statusBreakdown.passCond }}
                  </span>
                  <span v-if="(task.statusBreakdown?.fail || 0) > 0" class="run-stat run-stat-fail">
                    <i class="fas fa-times me-1"></i>Fail {{ task.statusBreakdown.fail }}
                  </span>
                  <span v-if="(task.statusBreakdown?.blocked || 0) > 0" class="run-stat run-stat-blocked">
                    <i class="fas fa-ban me-1"></i>Blocked {{ task.statusBreakdown.blocked }}
                  </span>
                  <span v-if="(task.statusBreakdown?.notRun || 0) > 0" class="run-stat run-stat-notrun">
                    <i class="fas fa-minus me-1"></i>Not Run {{ task.statusBreakdown.notRun }}
                  </span>
                  <span v-if="task.progress === 100" class="run-stat run-stat-complete">
                    <i class="fas fa-flag-checkered me-1"></i>Complete
                  </span>
                </div>
              </template>
              <template v-else>
                <p class="text-xs text-secondary mb-0" style="font-style:italic;">
                  <i class="fas fa-info-circle me-1"></i>No test cases added to this run yet.
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Detail modal -->
    <Teleport to="body">
      <div v-if="showTaskDetailModal" class="add-task-modal-overlay">
        <div class="add-task-modal-box" :class="{ 'add-task-modal-box--split': !detailLoading }">
          <div class="p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">
              {{ detailTask?.taskType === "run" ? "Test Run Detail" : "Task Detail" }}
            </h6>
          </div>
          <div v-if="detailLoading" class="p-4 text-center text-secondary text-sm">
            <i class="fas fa-spinner fa-spin me-1"></i>Loading...
          </div>
          <div v-else class="mytask-modal-split-body">
          <div class="mytask-modal-pane-form p-3">
            <div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Task Name</label>
                <input v-model="detailForm.name" type="text" class="form-control form-control-sm" :disabled="detailReadonly" />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Task Type</label>
                <input :value="detailTask?.taskType === 'plan' ? formatPlanType(detailTask?.planType) : 'Test Run'" type="text" class="form-control form-control-sm" disabled />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Project / Sub-Project</label>
                <input
                  :value="`${detailTask?.projectName || '-'} / ${detailTask?.subProjectName || '-'}`"
                  type="text"
                  class="form-control form-control-sm"
                  disabled
                />
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Start Date</label>
                  <input v-model="detailForm.planOverallStart" type="date" class="form-control form-control-sm" :disabled="detailReadonly" />
                </div>
                <div class="col-6 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                  <input v-model="detailForm.planOverallDue" type="date" class="form-control form-control-sm" :disabled="detailReadonly" />
                </div>
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
                <!--
                  Replaced native <select> with <base-select> so the opened
                  menu matches the rest of the app's design system instead
                  of the OS-native dark popup.
                -->
                <base-select
                  size="sm"
                  :model-value="detailForm.status"
                  :options="taskStatusOptions"
                  option-label="label"
                  option-value="value"
                  :disabled="detailReadonly"
                  placeholder="— Select status —"
                  @update:model-value="(v) => { detailForm.status = v == null ? '' : v; }"
                />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Details</label>
                <textarea v-model="detailForm.details" class="form-control form-control-sm" rows="3" :disabled="detailReadonly"></textarea>
              </div>
              <div v-if="detailTask?.taskType === 'run'" class="text-xs text-secondary"></div>
            </div>
          </div>
          <div class="mytask-modal-pane-jira p-3">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <h6 class="text-sm font-weight-bold mb-0"><i class="fab fa-jira text-primary me-1"></i>Jira</h6>
              <div v-if="detailTask?.taskType === 'plan' && !detailReadonly" class="mytask-jira-actions">
                <button type="button" class="btn btn-outline-primary mb-0" @click="openJiraActionModal('link', 'detail')">
                  <i class="fas fa-link me-1"></i>Link
                </button>
                <button type="button" class="btn btn-outline-info mb-0" @click="openJiraActionModal('createTask', 'detail')">
                  <i class="fab fa-jira me-1"></i>Task
                </button>
                <button type="button" class="btn btn-outline-warning mb-0" @click="openJiraActionModal('createBug', 'detail')">
                  <i class="fab fa-jira me-1"></i>Bug
                </button>
              </div>
            </div>
            <div v-if="detailTaskJiraLoading" class="text-xs text-secondary py-2">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading…
            </div>
            <div v-else-if="!detailTaskJiraLinks.length" class="text-xs text-secondary py-2">No Jira linked to this task</div>
            <div v-else class="mytask-jira-table-wrap">
              <table class="table table-sm mb-0 mytask-jira-table">
                <thead>
                  <tr>
                    <th class="mytask-jira-col-id">ID</th>
                    <th class="mytask-jira-col-title">Title</th>
                    <th class="mytask-jira-col-status">Status</th>
                    <th v-if="!detailReadonly" class="text-center mytask-jira-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="j in detailTaskJiraLinks" :key="j.key">
                    <td class="mytask-jira-col-id">
                      <a :href="j.url" target="_blank" rel="noopener" class="text-xs font-weight-bold">{{ j.key }}</a>
                    </td>
                    <td class="mytask-jira-col-title text-xs text-secondary text-truncate">{{ j.summary || "—" }}</td>
                    <td class="mytask-jira-col-status">
                      <jira-status-select
                        :issue-key="j.key"
                        :status="j.status || ''"
                        :transitions="j.transitions || []"
                        compact
                        mini
                        @update:status="j.status = $event"
                        @update:transitions="j.transitions = $event"
                      />
                    </td>
                    <td v-if="!detailReadonly" class="text-center mytask-jira-col-action">
                      <button type="button" class="btn btn-link btn-sm p-0 action-delete" title="Remove link" @click="removeDetailTaskJiraLink(j.key)">
                        <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="jiraCreateError" class="text-danger text-xs mt-2">{{ jiraCreateError }}</div>
          </div>
          </div>
          <div class="p-3 border-top">
            <div v-if="detailError" class="alert alert-danger py-2 px-3 mb-2 text-xs">{{ detailError }}</div>
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div class="d-flex gap-2">
                <button
                  v-if="isMyTaskDeletable(detailTask)"
                  type="button"
                  class="btn btn-sm btn-outline-danger mb-0"
                  :disabled="detailDeleting || detailSaving"
                  @click="requestDeleteMyTask(detailTask)"
                >
                  <span v-if="detailDeleting"><i class="fas fa-spinner fa-spin me-1"></i>Deleting...</span>
                  <span v-else><i class="fas fa-trash-alt me-1"></i>Delete</span>
                </button>
                <button
                  v-if="showPlanManagementButton"
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 nowrap-btn"
                  @click.stop="goToPlanManagement"
                >
                  Go To Plan Management
                </button>
                <button
                  v-if="showGoToTestRunButton"
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 nowrap-btn"
                  @click.stop="goToTestRun"
                >
                  <i class="fas fa-play-circle me-1"></i>Go To Test Run
                </button>
              </div>
              <div class="ms-auto d-flex gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeTaskDetailModal">Close</button>
                <button v-if="!detailReadonly" type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="detailSaving" @click="saveTaskDetail">
                  <span v-if="detailSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                  <span v-else>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Jira issue detail (split modal — same pattern as Task Detail / Add Task) -->
    <Teleport to="body">
      <div v-if="showJiraIssueModal && selectedJiraIssue" class="add-task-modal-overlay">
        <div class="add-task-modal-box add-task-modal-box--split">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Jira Issue Detail</h6>
          </div>
          <div class="mytask-modal-split-body">
            <div class="mytask-modal-pane-form p-3">
              <h6 class="text-sm font-weight-bold mb-3">
                <i class="fab fa-jira text-primary me-1"></i>Jira
              </h6>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Issue Key</label>
                <input :value="selectedJiraIssue.key" type="text" class="form-control form-control-sm" disabled />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Summary</label>
                <input :value="selectedJiraIssue.summary || '—'" type="text" class="form-control form-control-sm" disabled />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
                <jira-status-select
                  :issue-key="selectedJiraIssue.key"
                  :status="selectedJiraIssue.status || ''"
                  :transitions="selectedJiraIssue.transitions || []"
                  @update:status="onJiraModalStatusUpdate"
                  @update:transitions="onJiraModalTransitionsUpdate"
                  @error="onJiraModalFieldError"
                />
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Issue Type</label>
                  <input :value="selectedJiraIssue.issueType || '—'" type="text" class="form-control form-control-sm" disabled />
                </div>
                <div class="col-6 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                  <input
                    v-model="jiraModalDueDate"
                    type="date"
                    class="form-control form-control-sm"
                    :disabled="jiraModalSavingDue"
                    @change="saveJiraModalDueDate"
                  />
                  <span v-if="jiraModalSavingDue" class="text-xs text-secondary mt-1 d-block">
                    <i class="fas fa-spinner fa-spin me-1"></i>Saving...
                  </span>
                </div>
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Project / Sprint</label>
                <input
                  :value="jiraIssueProjectSprintLabel(selectedJiraIssue)"
                  type="text"
                  class="form-control form-control-sm"
                  disabled
                />
              </div>
            </div>
            <div class="mytask-modal-pane-side p-3">
              <h6 class="text-sm font-weight-bold mb-2">
                <i class="fas fa-link text-primary me-1"></i>Linked in TestOps
              </h6>
              <p v-if="jiraModalLinkedItems.length" class="text-xs text-secondary mb-2">
                {{ jiraModalLinkedItems.length }} item(s) linked to this Jira issue
              </p>
              <div v-if="jiraModalLinkedItems.length === 0" class="mytask-side-empty text-xs text-secondary">
                <i class="fas fa-unlink opacity-5 mb-2 d-block" style="font-size:1.5rem;"></i>
                No Test Plan or Task linked to this Jira issue.
              </div>
              <div v-else class="mytask-side-table-wrap">
                <table class="table table-sm mb-0 mytask-side-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Page</th>
                      <th>Name</th>
                      <th>Sub-Project</th>
                      <th>Status</th>
                      <th class="text-center">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="link in jiraModalLinkedItems" :key="jiraLinkRowKey(link)">
                      <td>
                        <span class="task-type-badge" :class="link.linkKind === 'test_plan' ? 'badge-plan' : 'badge-run'">
                          {{ link.linkKind === "test_plan" ? "Plan" : "Task" }}
                        </span>
                      </td>
                      <td class="text-xs text-secondary mytask-side-page">{{ jiraLinkPageLabel(link) }}</td>
                      <td class="mytask-side-name">{{ jiraLinkedLabel(link) }}</td>
                      <td class="text-xs text-secondary mytask-side-sub">
                        {{ link.subProjectName || "—" }}
                      </td>
                      <td class="mytask-side-status">
                        <base-select
                          v-if="link.taskId"
                          size="sm"
                          :model-value="normalizeTaskStatusValue(link.taskStatus)"
                          :options="taskStatusOptions"
                          option-label="label"
                          option-value="value"
                          :disabled="linkedTaskStatusSavingId === link.taskId"
                          placeholder="— Status —"
                          @update:model-value="(v) => updateLinkedTaskStatus(link, v)"
                        />
                        <span v-else class="text-xs text-secondary">—</span>
                      </td>
                      <td class="text-center">
                        <router-link
                          v-if="link.projectPlanId"
                          :to="'/test-plan/' + link.projectPlanId"
                          class="btn btn-link btn-sm p-0 text-primary mb-0"
                          title="Open Plan Management"
                          @click="closeJiraIssueModal"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </router-link>
                        <span v-else class="text-secondary">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="p-3 border-top">
            <div v-if="jiraModalError" class="alert alert-danger py-2 px-3 mb-2 text-xs">{{ jiraModalError }}</div>
            <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
              <button
                v-if="selectedJiraIssue.url"
                type="button"
                class="btn btn-sm btn-outline-primary mb-0 nowrap-btn"
                @click="openJiraIssueExternal"
              >
                <i class="fab fa-jira me-1"></i>Open in Jira
              </button>
              <div class="ms-auto d-flex gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeJiraIssueModal">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Add Task Item modal (same pattern as Test Plan > Add Plan Item) -->
    <Teleport to="body">
      <div v-if="showAddTaskModal" class="add-task-modal-overlay">
        <div class="add-task-modal-box add-task-modal-box--split">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Add Task Item</h6>
          </div>
          <div class="mytask-modal-split-body">
          <div class="mytask-modal-pane-form p-3">
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Task Item <span class="text-danger">*</span></label>
              <input v-model="addTaskForm.taskItem" type="text" class="form-control form-control-sm" placeholder="Enter task item" />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Task Type <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                :model-value="addTaskForm.taskType"
                :options="taskTypeOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => { addTaskForm.taskType = v == null ? 'plan' : v; }"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Project <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                :model-value="addTaskForm.projectId || null"
                :options="projects.map((p) => ({ value: String(p.projectId), label: p.projectName }))"
                option-label="label"
                option-value="value"
                placeholder="— Select project —"
                @update:model-value="(v) => { addTaskForm.projectId = v == null ? '' : v; onAddTaskProjectChange(); }"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Sub-Project <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                :model-value="addTaskForm.subProjectId || null"
                :options="subProjectsFiltered.map((s) => ({ value: String(s.subProjectId), label: s.subProjectName }))"
                option-label="label"
                option-value="value"
                :disabled="!addTaskForm.projectId"
                placeholder="— Select sub-project —"
                @update:model-value="(v) => { addTaskForm.subProjectId = v == null ? '' : v; onAddTaskSubProjectChange(); }"
              />
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Start Date</label>
                <input v-model="addTaskForm.planOverallStart" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                <input v-model="addTaskForm.planOverallDue" type="date" class="form-control form-control-sm" />
              </div>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Owner Task</label>
              <input type="text" class="form-control form-control-sm" :value="currentUserName" disabled />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Details</label>
              <textarea v-model="addTaskForm.details" class="form-control form-control-sm add-task-details-textarea" rows="3" placeholder="Enter details..."></textarea>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
              <base-select
                size="sm"
                :model-value="addTaskForm.status"
                :options="taskStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select status —"
                @update:model-value="(v) => { addTaskForm.status = v == null ? '' : v; }"
              />
            </div>
          </div>
          <div class="mytask-modal-pane-jira p-3">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <h6 class="text-sm font-weight-bold mb-0"><i class="fab fa-jira text-primary me-1"></i>Jira</h6>
              <div v-if="addTaskForm.subProjectId" class="mytask-jira-actions">
                <button type="button" class="btn btn-outline-primary mb-0" @click="openJiraActionModal('link', 'add')">
                  <i class="fas fa-link me-1"></i>Link
                </button>
                <button type="button" class="btn btn-outline-info mb-0" @click="openJiraActionModal('createTask', 'add')">
                  <i class="fab fa-jira me-1"></i>Task
                </button>
                <button type="button" class="btn btn-outline-warning mb-0" @click="openJiraActionModal('createBug', 'add')">
                  <i class="fab fa-jira me-1"></i>Bug
                </button>
              </div>
            </div>
            <div v-if="addTaskProjectPlanLoading" class="text-xs text-secondary py-2">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading…
            </div>
            <template v-else-if="!addTaskForm.subProjectId">
              <p class="text-xs text-secondary mb-0">เลือก Sub-Project ก่อน</p>
            </template>
            <template v-else-if="addTaskForm.subProjectId">
              <div v-if="addTaskLinkedJiraLoading" class="text-xs text-secondary py-2">
                <i class="fas fa-spinner fa-spin me-1"></i>Loading…
              </div>
              <div v-else-if="!addTaskLinkedJira.length" class="text-xs text-secondary py-2">No Jira linked to this task</div>
              <div v-else class="mytask-jira-table-wrap">
                <table class="table table-sm mb-0 mytask-jira-table">
                  <thead>
                    <tr>
                      <th class="mytask-jira-col-id">ID</th>
                      <th class="mytask-jira-col-title">Title</th>
                      <th class="mytask-jira-col-status">Status</th>
                      <th class="text-center mytask-jira-col-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="j in addTaskLinkedJira" :key="j.key">
                      <td class="mytask-jira-col-id">
                        <a :href="j.url" target="_blank" rel="noopener" class="text-xs font-weight-bold">{{ j.key }}</a>
                      </td>
                      <td class="mytask-jira-col-title text-xs text-secondary text-truncate">{{ j.summary || "—" }}</td>
                      <td class="mytask-jira-col-status">
                        <jira-status-select
                          :issue-key="j.key"
                          :status="j.status || ''"
                          :transitions="j.transitions || []"
                          compact
                          mini
                          @update:status="j.status = $event"
                          @update:transitions="j.transitions = $event"
                        />
                      </td>
                      <td class="text-center mytask-jira-col-action">
                        <button type="button" class="btn btn-link btn-sm p-0 action-delete" title="Remove link" @click="removeAddTaskJiraLink(j.key)">
                          <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="jiraCreateError" class="text-danger text-xs mt-2">{{ jiraCreateError }}</div>
            </template>
          </div>
          </div>
          <div class="p-3 border-top">
            <div v-if="addTaskSaveError" class="alert alert-danger py-2 px-3 mb-2 text-xs">{{ addTaskSaveError }}</div>
            <div v-if="addTaskJiraWarning" class="alert alert-warning py-2 px-3 mb-2 text-xs" style="white-space:pre-wrap;">{{ addTaskJiraWarning }}</div>
            <div class="d-flex align-items-center justify-content-end gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeAddTaskModal">Cancel</button>
              <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="addTaskSaving" @click="saveAddTask">
                <span v-if="addTaskSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                <span v-else>Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete confirm (in-app modal — not browser confirm) -->
    <Teleport to="body">
      <div v-if="showDeleteConfirm" class="add-task-modal-overlay">
        <div class="add-task-modal-box" style="width: 420px; max-height: none;">
          <div class="p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Delete Task</h6>
          </div>
          <div class="p-3">
            <p class="text-sm mb-0" style="color:#344767;">
              Delete task <strong>{{ deleteConfirmTask?.name }}</strong>?
            </p>
            <p class="text-xs text-secondary mb-0 mt-2">This action cannot be undone.</p>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="detailDeleting" @click="cancelDeleteConfirm">
              Cancel
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger mb-0" :disabled="detailDeleting" @click="confirmDeleteMyTask">
              <span v-if="detailDeleting"><i class="fas fa-spinner fa-spin me-1"></i>Deleting...</span>
              <span v-else><i class="fas fa-trash-alt me-1"></i>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Jira link / create modal -->
    <Teleport to="body">
      <div v-if="showJiraActionModal" class="add-task-modal-overlay">
        <div class="add-task-modal-box" style="width: 480px; max-height: none;">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ jiraActionModalTitle }}</h6>
          </div>
          <div class="p-3">
            <label class="text-xs font-weight-bold mb-1 d-block">Jira Space <span class="text-danger">*</span></label>
            <base-select
              size="sm"
              :model-value="jiraActionProjectKey || null"
              :options="jiraProjects.map((jp) => ({ value: jp.key, label: `${jp.name} (${jp.key})` }))"
              option-label="label"
              option-value="value"
              placeholder="— Select Space —"
              @update:model-value="(v) => { jiraActionProjectKey = v == null ? '' : v; onJiraActionProjectChange(); }"
            />
            <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
            </span>
            <template v-if="jiraActionIsCreate">
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Issue Title <span class="text-danger">*</span></label>
                <input v-model="jiraActionTitle" type="text" class="form-control form-control-sm" placeholder="หัวข้อ issue" />
              </div>
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Description <span class="text-danger">*</span></label>
                <textarea v-model="jiraActionDescription" class="form-control form-control-sm" rows="4" placeholder="รายละเอียด issue" style="resize:vertical;"></textarea>
              </div>
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Assignee</label>
                <base-select
                  size="sm"
                  :model-value="jiraActionAssigneeAccountId || null"
                  :options="jiraAssignableUsers"
                  option-label="displayName"
                  option-value="accountId"
                  placeholder="— ไม่ระบุ —"
                  clearable
                  clear-label="— ไม่ระบุ —"
                  searchable
                  search-placeholder="ค้นหาชื่อ…"
                  :disabled="!jiraActionProjectKey || jiraLoadingAssignable"
                  empty-label="ไม่พบผู้ใช้"
                  @update:model-value="(v) => { jiraActionAssigneeAccountId = v == null ? '' : v; }"
                />
                <span v-if="jiraLoadingAssignable" class="text-xs text-secondary mt-1 d-block">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading users…
                </span>
              </div>
            </template>
            <div v-if="jiraActionProjectKey" class="mt-3">
              <label class="text-xs font-weight-bold mb-1 d-block">
                Jira Tickets
                <span v-if="jiraActionMode === 'link'" class="text-danger">*</span>
                <span v-else class="text-secondary font-weight-normal">(optional — relate to parent)</span>
              </label>
              <div v-if="jiraActionIssueKeys.length" class="d-flex flex-wrap gap-1 mb-2">
                <span v-for="key in jiraActionIssueKeys" :key="key" class="jira-tag">
                  <i class="fab fa-jira me-1"></i>{{ key }}
                  <button type="button" class="jira-tag-remove" @click="removeJiraActionTicket(key)">×</button>
                </span>
              </div>
              <input
                v-model="jiraActionSearch"
                type="text"
                class="form-control form-control-sm mb-1"
                placeholder="Search ticket..."
                @input="onJiraActionSearch"
              />
              <div class="jira-issue-list">
                <div v-if="jiraLoadingIssues" class="text-center py-2 text-secondary text-xs">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                </div>
                <div v-else-if="jiraIssues.length === 0" class="text-center py-2 text-secondary text-xs">No tickets found</div>
                <div
                  v-for="i in jiraIssues"
                  :key="i.key"
                  class="jira-issue-item"
                  :class="{ 'jira-issue-selected': jiraActionIssueKeys.includes(i.key) }"
                  @click="toggleJiraActionTicket(i.key)"
                >
                  <i class="fas fa-check-circle me-1 text-success" v-if="jiraActionIssueKeys.includes(i.key)"></i>
                  <i class="fas fa-circle me-1" style="font-size:0.5rem; color:transparent;" v-else></i>
                  <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
                  <span class="text-xs">{{ i.summary }}</span>
                  <span v-if="i.status" class="text-xs text-secondary ms-1">({{ i.status }})</span>
                </div>
              </div>
            </div>
            <div v-if="jiraActionError" class="text-danger text-xs mt-2">{{ jiraActionError }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="jiraCreatingIssue" @click="closeJiraActionModal">Cancel</button>
            <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="jiraActionConfirmDisabled" @click="confirmJiraAction">
              <span v-if="jiraCreatingIssue"><i class="fas fa-spinner fa-spin me-1"></i>Working...</span>
              <span v-else>{{ jiraActionConfirmLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { API_BASE_URL, apiHeaders, fetchWithTimeout } from "@/config/apiBase";
import { myTasksApi, projectsApi, subProjectsApi, usersApi, testPlanItemsApi, testRunsApi } from "@/services/backendApi";
import { mapMutations, mapState } from "vuex";
import {
  statusLabel as workflowStatusLabel,
  statusPillClass as workflowStatusPillClass,
  isWorkflowStatusKey,
  normalizeSubProjectStatus,
} from "@/utils/subProjectStatus";
import JiraLinksPanel from "@/components/JiraLinksPanel.vue";
import JiraStatusSelect from "@/components/JiraStatusSelect.vue";
import { parseJiraLinks } from "@/utils/jiraLinksPayload";
import {
  enrichJiraLinkRows,
  fetchJiraIssuesByKeys,
  fetchJiraMyIssuesCategories,
  fetchJiraTransitions,
  updateJiraDueDate,
} from "@/services/jiraStatusApi";

export default {
  name: "MyTask",
  components: { JiraLinksPanel, JiraStatusSelect },
  data() {
    return {
      loading: false,
      _loadTasksSeq: 0,
      tasksLoadError: null,
      tasks: [],
      activeFilter: "all",
      expandedJiraKeys: {},
      showAddTaskModal: false,
      addTaskSaving: false,
      addTaskSaveError: null,
      addTaskJiraWarning: null,
      addTaskProjectPlanLoading: false,
      showTaskDetailModal: false,
      showJiraIssueModal: false,
      selectedJiraIssue: null,
      jiraModalDueDate: "",
      jiraModalSavingDue: false,
      jiraModalError: null,
      linkedTaskStatusSavingId: null,
      detailTask: null,
      detailScopeJiraLinks: [],
      detailTaskJiraLinks: [],
      detailJiraLinks: [],
      detailLoading: false,
      detailSaving: false,
      detailDeleting: false,
      detailError: null,
      detailForm: {
        name: "",
        planOverallStart: "",
        planOverallDue: "",
        status: "to_do",
        details: "",
      },
      resolvedProjectPlanId: null,
      projects: [],
      allSubProjects: [],
      subProjectsFiltered: [],
      users: [],
      subProjectJiraLinks: [],
      addTaskLinkedJira: [],
      addTaskLinkedJiraLoading: false,
      detailTaskJiraLoading: false,
      showDeleteConfirm: false,
      deleteConfirmTask: null,
      showJiraActionModal: false,
      jiraActionMode: null,
      jiraActionContext: null,
      jiraActionProjectKey: "",
      jiraActionIssueKeys: [],
      jiraActionSearch: "",
      jiraActionError: null,
      jiraActionSearchTimer: null,
      jiraActionTitle: "",
      jiraActionDescription: "",
      jiraActionAssigneeAccountId: "",
      jiraAssignableUsers: [],
      jiraLoadingAssignable: false,
      jiraCreatingIssue: false,
      jiraCreateError: null,
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      // Jira tabs (within MyTask page)
      jiraAssignedIssues: [],
      jiraReporterIssues: [],
      jiraSubTab: "assigned", // assigned | reporter | future
      jiraProjectFilterKeyByTab: { assigned: "all", reporter: "all", future: "all" },
      jiraSprintCollapsedMap: {},
      jiraConnectedMeta: null,
      jiraLoadingAssigned: false,
      jiraLoadError: null,
      addTaskForm: {
        taskItem: "",
        taskType: "support",
        projectId: "",
        subProjectId: "",
        planOverallStart: "",
        planOverallDue: "",
        userIds: [],
        details: "",
        status: "to_do",
        createJiraSubtask: false,
        jiraProjectKey: "",
        jiraIssueKeys: [],
      },
    };
  },
  computed: {
    ...mapState(["user"]),
    addTaskHasScopeJira() {
      return (this.subProjectJiraLinks || []).length > 0;
    },
    detailHasScopeJira() {
      return (this.detailScopeJiraLinks || []).length > 0;
    },
    jiraActionIsCreate() {
      return this.jiraActionMode === "createTask" || this.jiraActionMode === "createBug";
    },
    jiraActionModalTitle() {
      if (this.jiraActionMode === "createTask") return "Create Jira Task";
      if (this.jiraActionMode === "createBug") return "Create Jira Bug";
      return "Link Jira";
    },
    jiraActionConfirmLabel() {
      if (this.jiraActionMode === "createTask") return "Create Task";
      if (this.jiraActionMode === "createBug") return "Create Bug";
      return "Link";
    },
    jiraActionConfirmDisabled() {
      if (this.jiraCreatingIssue) return true;
      if (!this.jiraActionProjectKey) return true;
      if (this.jiraActionMode === "link") return !this.jiraActionIssueKeys.length;
      if (!String(this.jiraActionTitle || "").trim()) return true;
      if (!String(this.jiraActionDescription || "").trim()) return true;
      return false;
    },
    filteredTasks() {
      if (this.activeFilter === "all") {
        const dueMs = (d) =>
          d ? new Date(`${String(d).slice(0, 10)}T00:00:00Z`).getTime() : Infinity;
        const testOps = (this.tasks || []).map((t) => ({ ...t, _sortDueMs: dueMs(t.dueDate) }));
        const jiraCards = (this.jiraIssuesForAllTab || []).map((issue) => {
          const sprint = this.jiraIssueSprint(issue);
          return {
            taskType: "jira",
            id: issue.key,
            name: issue.summary || issue.key,
            dueDate: issue.dueDate || null,
            status: issue.status || "",
            issueType: issue.issueType || "",
            projectName: issue.projectName || "",
            projectKey: issue.projectKey || "",
            sprintName: sprint?.name || "No Sprint",
            jiraIssue: issue,
            _sortDueMs: dueMs(issue.dueDate),
          };
        });
        return [...testOps, ...jiraCards].sort((a, b) => a._sortDueMs - b._sortDueMs);
      }
      if (this.activeFilter === "task") {
        return this.tasks.filter(
          (t) => t.taskType === "plan" && String(t.planType || "plan").toLowerCase() === "plan"
        );
      }
      if (this.activeFilter === "plan") {
        return this.tasks.filter((t) =>
          t.taskType === "plan" && ["support", "other"].includes(String(t.planType || "").toLowerCase())
        );
      }
      return this.tasks.filter(t => t.taskType === this.activeFilter);
    },
    overdueCount() {
      return this.tasks.filter(
        (t) => !this.taskStatusExemptFromOverdue(t.status) && this.isOverdue(t.dueDate)
      ).length;
    },
    jiraAccountEmailMismatch() {
      const login = String(this.user?.email || "").trim().toLowerCase();
      const jira = String(this.jiraConnectedMeta?.email || "").trim().toLowerCase();
      return !!(login && jira && login !== jira);
    },
    jiraModalLinkedItems() {
      const links = this.selectedJiraIssue?.linkedPlanTasks;
      if (!Array.isArray(links)) return [];
      const seen = new Set();
      return links.filter((link) => {
        const id = this.jiraLinkRowKey(link);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    jiraIssuesForSubTab() {
      const tab = String(this.jiraSubTab || "assigned").trim();
      if (tab === "reporter") return Array.isArray(this.jiraReporterIssues) ? this.jiraReporterIssues : [];
      if (tab === "future") {
        const byKey = new Map();
        for (const row of this.jiraAssignedIssues || []) {
          const k = String(row?.key || "").trim().toUpperCase();
          if (k) byKey.set(k, row);
        }
        for (const row of this.jiraReporterIssues || []) {
          const k = String(row?.key || "").trim().toUpperCase();
          if (k && !byKey.has(k)) byKey.set(k, row);
        }
        return [...byKey.values()];
      }
      return Array.isArray(this.jiraAssignedIssues) ? this.jiraAssignedIssues : [];
    },
    jiraProjectFilterKeyForTab: {
      get() {
        const tab = String(this.jiraSubTab || "assigned").trim();
        const map = this.jiraProjectFilterKeyByTab || {};
        return map[tab] || "all";
      },
      set(v) {
        const tab = String(this.jiraSubTab || "assigned").trim();
        const next = { ...(this.jiraProjectFilterKeyByTab || {}) };
        next[tab] = v || "all";
        this.jiraProjectFilterKeyByTab = next;
      },
    },
    jiraVisibleIssues() {
      let issues = Array.isArray(this.jiraIssuesForSubTab) ? this.jiraIssuesForSubTab : [];
      const pk = String(this.jiraProjectFilterKeyForTab || "all").trim();
      if (pk && pk !== "all") {
        issues = issues.filter((i) => String(i.projectKey || "") === pk);
      }
      const tab = String(this.jiraSubTab || "assigned").trim();
      issues = issues.filter((i) => this.issueMatchesJiraSubTabSprint(i, tab));
      return issues;
    },
    jiraProjectFilterOptionsForTab() {
      const all = Array.isArray(this.jiraIssuesForSubTab) ? this.jiraIssuesForSubTab : [];
      const byProject = new Map();
      for (const issue of all) {
        const key = String(issue.projectKey || "").trim();
        if (!key) continue;
        if (!byProject.has(key)) byProject.set(key, String(issue.projectName || key));
      }
      const items = [...byProject.entries()]
        .map(([value, name]) => ({ value, label: name ? `${name} (${value})` : value }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
      return [{ value: "all", label: "All projects" }, ...items];
    },
    jiraProjectGroups() {
      const issues = Array.isArray(this.jiraVisibleIssues) ? this.jiraVisibleIssues : [];
      const dueMs = (d) => (d ? new Date(`${d}T00:00:00Z`).getTime() : Infinity);

      const byProject = new Map();
      for (const issue of issues) {
        const projectKey = String(issue.projectKey || "").trim() || "unknown";
        const projectName = issue.projectName || projectKey;

        if (!byProject.has(projectKey)) {
          byProject.set(projectKey, {
            projectKey,
            projectName,
            sprintGroupsMap: new Map(),
          });
        }

        const proj = byProject.get(projectKey);

        const sprint = issue.nearestSprint || issue.sprint || null;
        const sprintId = sprint?.id != null ? String(sprint.id) : null;
        const sprintKey = sprintId ? `id:${sprintId}` : "nosprint";
        const sprintName = sprint?.name ? String(sprint.name) : "No Sprint";
        const sprintState = sprint?.state ? String(sprint.state) : "";

        if (!proj.sprintGroupsMap.has(sprintKey)) {
          proj.sprintGroupsMap.set(sprintKey, {
            sprintKey,
            sprintName,
            sprintState,
            issues: [],
          });
        }

        proj.sprintGroupsMap.get(sprintKey).issues.push(issue);
      }

      const projects = [...byProject.values()].map((proj) => {
        let sprintGroups = [...proj.sprintGroupsMap.values()].filter(
          (sg) => !this.isJiraSprintClosed({ state: sg.sprintState })
        );

        for (const sg of sprintGroups) {
          sg.issues.sort((a, b) => dueMs(a.dueDate) - dueMs(b.dueDate));
          sg.minDueMs = sg.issues.reduce((min, it) => Math.min(min, dueMs(it.dueDate)), Infinity);
        }

        sprintGroups = sprintGroups.filter((sg) => sg.issues.length > 0);
        sprintGroups.sort((a, b) => a.minDueMs - b.minDueMs);
        proj.minDueMs = sprintGroups.reduce((min, sg) => Math.min(min, sg.minDueMs), Infinity);

        return {
          projectKey: proj.projectKey,
          projectName: proj.projectName,
          sprintGroups,
          minDueMs: proj.minDueMs,
        };
      });

      return projects
        .filter((p) => p.sprintGroups && p.sprintGroups.length > 0)
        .sort((a, b) => a.minDueMs - b.minDueMs);
    },
    jiraTabIssueCount() {
      return (this.jiraVisibleIssues || []).length;
    },
    jiraTabOverdueCount() {
      return (this.jiraVisibleIssues || []).filter(
        (i) => i.dueDate && this.isOverdue(i.dueDate) && !this.taskStatusExemptFromOverdue(i.status)
      ).length;
    },
    jiraTabLinkedCount() {
      return (this.jiraVisibleIssues || []).filter((i) => this.jiraLinkedCount(i) > 0).length;
    },
    jiraWorkWeekLabel() {
      const { mon, fri } = this.currentWorkWeekRange();
      const fmt = (d) => {
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        return `${day}/${month}`;
      };
      return `จ–ศ (${fmt(mon)}–${fmt(fri)})`;
    },
    jiraKeysOnTestOpsTaskCards() {
      const keys = new Set();
      for (const t of this.tasks || []) {
        for (const link of t.jiraLinks || []) {
          const k = String(link?.key || "").trim().toUpperCase();
          if (k) keys.add(k);
        }
      }
      return keys;
    },
    jiraIssuesForAllTab() {
      const linkedOnCards = this.jiraKeysOnTestOpsTaskCards;
      const byKey = new Map();
      const add = (row) => {
        const k = String(row?.key || "").trim().toUpperCase();
        if (!k || byKey.has(k)) return;
        if (linkedOnCards.has(k)) return;
        if (this.jiraLinkedCount(row) > 0) return;
        if (!this.issueQualifiesForAllTab(row)) return;
        byKey.set(k, row);
      };
      for (const row of this.jiraAssignedIssues || []) add(row);
      for (const row of this.jiraReporterIssues || []) add(row);
      return [...byKey.values()];
    },
    currentUserName() {
      const u = this.user || {};
      if (u.firstName || u.lastName) return `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return u.email || "";
    },
    detailReadonly() {
      return this.detailTask?.taskType === "run";
    },
    showPlanManagementButton() {
      if (this.detailTask?.taskType !== "plan") return false;
      const type = String(this.detailTask?.planType || "plan").toLowerCase();
      return type !== "support" && type !== "other";
    },
    showGoToTestRunButton() {
      if (this.detailTask?.taskType !== "run") return false;
      const planId = this.detailTask?.projectPlanId ?? this.detailTask?.testPlanId;
      const runId = this.detailTask?.runId ?? this.detailTask?.id;
      return !!(planId && runId);
    },
    /**
     * Centralized option lists for the in-app <base-select> dropdowns
     * used by both the "Task Detail" and "Add Task Item" modals.
     */
    taskStatusOptions() {
      return [
        { value: "draft", label: "Draft" },
        { value: "to_do", label: "To Do" },
        { value: "in_progress", label: "In Progress" },
        { value: "ready_to_test_sit", label: "Ready To Test on SIT" },
        { value: "testing_on_sit", label: "Testing on SIT" },
        { value: "ready_to_test_stg", label: "Ready To Test on STG" },
        { value: "testing_on_stg", label: "Testing on STG" },
        { value: "ready_to_deploy", label: "Ready To Deplay" },
        { value: "done", label: "Done" },
        { value: "deployed", label: "Deployed" },
        { value: "block", label: "Block" },
        { value: "hold", label: "Hold" },
        { value: "cancel", label: "Cancel" },
      ];
    },
    taskTypeOptions() {
      return [
        { value: "plan", label: "Plan" },
        { value: "support", label: "Support" },
        { value: "other", label: "Other" },
      ];
    },
  },

  mounted() {
    if (this.loading) void this.loadTasks();
  },
  watch: {
    "user.userId": {
      immediate: true,
      handler(userId) {
        const id = Number(userId);
        if (!Number.isFinite(id) || id <= 0) {
          this.loading = false;
          return;
        }
        void this.loadTasks();
        void this.loadJiraIssues();
      },
    },
    "user.jiraAccountId": {
      immediate: true,
      handler() {
        if (this.activeFilter === "jira" || this.activeFilter === "all") this.loadJiraIssues();
      },
    },
    activeFilter: {
      handler(v) {
        if (v === "jira" || v === "all") this.loadJiraIssues();
      },
    },
  },
  methods: {
    ...mapMutations(["setUser"]),
    async refreshSessionUser() {
      if (!this.user?.userId) return;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/me`, { headers: apiHeaders() });
        const data = await res.json();
        if (res.ok && data.user) {
          this.setUser({ user: data.user, token: data.token });
        }
      } catch (e) {
        console.error("refreshSessionUser:", e);
      }
    },
    async openAddTaskModal() {
      this.addTaskSaveError = null;
      this.addTaskJiraWarning = null;
      this.resetAddTaskForm();
      this.showAddTaskModal = true;
      try {
        const [projects, subs, users] = await Promise.all([
          projectsApi.getAll(),
          subProjectsApi.getAll(),
          usersApi.getAll(),
        ]);
        this.projects = projects;
        this.allSubProjects = subs;
        this.users = users;
      } catch (e) {
        console.error("openAddTaskModal load meta:", e);
      }
    },
    resetAddTaskForm() {
      this.resolvedProjectPlanId = null;
      this.subProjectJiraLinks = [];
      this.addTaskLinkedJira = [];
      this.jiraCreateError = null;
      this.jiraIssues = [];
      this.jiraSearch = "";
      this.addTaskForm = {
        taskItem: "",
        taskType: "support",
        projectId: "",
        subProjectId: "",
        planOverallStart: "",
        planOverallDue: "",
        userIds: [],
        details: "",
        status: "to_do",
        createJiraSubtask: false,
        jiraProjectKey: "",
        jiraIssueKeys: [],
      };
      this.subProjectsFiltered = [];
    },
    closeAddTaskModal() {
      this.showAddTaskModal = false;
      this.addTaskSaveError = null;
      this.addTaskJiraWarning = null;
      this.jiraCreateError = null;
    },
    onAddTaskProjectChange() {
      this.addTaskForm.subProjectId = "";
      this.resolvedProjectPlanId = null;
      this.subProjectJiraLinks = [];
      const pid = this.addTaskForm.projectId;
      if (!pid) {
        this.subProjectsFiltered = [];
        return;
      }
      this.subProjectsFiltered = this.allSubProjects.filter(
        (s) => String(s.projectId) === String(pid)
      );
    },
    async onAddTaskSubProjectChange() {
      this.resolvedProjectPlanId = null;
      this.subProjectJiraLinks = [];
      this.addTaskForm.jiraIssueKeys = [];
      const sid = this.addTaskForm.subProjectId;
      if (!sid) return;
      this.addTaskProjectPlanLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/project-plan/by-sub-project/${sid}`);
        if (res.ok) {
          const data = await res.json();
          this.resolvedProjectPlanId = data.projectPlanId;
        } else {
          // Allowed: Add Task can create a new project_plan server-side if none exists yet.
          this.resolvedProjectPlanId = null;
        }
        this.addTaskSaveError = null;
        if (this.resolvedProjectPlanId) {
          const pr = await fetchWithTimeout(`${API_BASE_URL}/api/project-plan/${this.resolvedProjectPlanId}`);
          if (pr.ok) {
            const row = await pr.json();
            const raw = row.projectLink ? String(row.projectLink).trim() : null;
            if (raw) {
              try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                  this.subProjectJiraLinks = parsed;
                  await this.enrichAddTaskJiraLinks();
                }
              } catch (_) { /* ignore */ }
            }
          }
        }
        if (this.subProjectJiraLinks.length === 0) await this.loadJiraProjectsForAddTask();
      } catch (e) {
        console.error(e);
        this.addTaskSaveError = e.message || "Could not load project plan.";
      } finally {
        this.addTaskProjectPlanLoading = false;
      }
    },
    onAddTaskJiraChange() {
      if (!this.addTaskForm.createJiraSubtask) {
        this.addTaskForm.jiraIssueKeys = [];
      }
    },
    async enrichAddTaskJiraLinks() {
      if (!this.subProjectJiraLinks.length) return;
      try {
        const data = await fetchJiraIssuesByKeys(this.subProjectJiraLinks.map((l) => l.key));
        this.subProjectJiraLinks = enrichJiraLinkRows(this.subProjectJiraLinks, data);
      } catch (_) { /* ignore */ }
    },
    async loadJiraProjectsForAddTask() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) {
        console.error(e);
      } finally {
        this.jiraLoadingProjects = false;
      }
    },
    onAddTaskJiraProjectChange() {
      this.addTaskForm.jiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
      if (this.addTaskForm.jiraProjectKey) this.loadJiraIssuesForAddTask(this.addTaskForm.jiraProjectKey);
    },
    async loadJiraIssuesForAddTask(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) {
        console.error(e);
      } finally {
        this.jiraLoadingIssues = false;
      }
    },
    async loadJiraIssues() {
      if (this.jiraLoadingAssigned) return;

      this.jiraLoadingAssigned = true;
      this.jiraLoadError = null;
      try {
        void this.refreshSessionUser();
        if (!this.user?.jiraAccountId) {
          this.jiraAssignedIssues = [];
          this.jiraReporterIssues = [];
          this.jiraConnectedMeta = null;
          return;
        }
        const data = await fetchJiraMyIssuesCategories(this.user?.userId);
        this.jiraAssignedIssues = data.assigned || [];
        this.jiraReporterIssues = data.reporter || [];
        this.jiraConnectedMeta = data.meta || null;
      } catch (e) {
        this.jiraLoadError = e?.message || "Failed to load Jira issues";
        this.jiraAssignedIssues = [];
        this.jiraReporterIssues = [];
        this.jiraConnectedMeta = null;
      } finally {
        this.jiraLoadingAssigned = false;
      }
    },
    jiraSprintCollapseKey(projectKey, sprintKey) {
      return `${projectKey}::${sprintKey}`;
    },
    isSprintCollapsed(projectKey, sprintKey) {
      const k = this.jiraSprintCollapseKey(projectKey, sprintKey);
      return !!this.jiraSprintCollapsedMap[k];
    },
    toggleSprintCollapsed(projectKey, sprintKey) {
      const k = this.jiraSprintCollapseKey(projectKey, sprintKey);
      const cur = !!this.jiraSprintCollapsedMap[k];
      this.jiraSprintCollapsedMap = { ...this.jiraSprintCollapsedMap, [k]: !cur };
    },
    resetJiraSprintCollapse() {
      this.jiraSprintCollapsedMap = {};
    },
    onAddTaskJiraSearch() {
      clearTimeout(this.jiraSearchTimer);
      this.jiraSearchTimer = setTimeout(() => {
        if (this.addTaskForm.jiraProjectKey) this.loadJiraIssuesForAddTask(this.addTaskForm.jiraProjectKey, this.jiraSearch);
      }, 400);
    },
    toggleAddTaskJira(key) {
      const i = this.addTaskForm.jiraIssueKeys.indexOf(key);
      if (i === -1) this.addTaskForm.jiraIssueKeys.push(key);
      else this.addTaskForm.jiraIssueKeys.splice(i, 1);
    },
    async saveAddTask() {
      this.addTaskSaveError = null;
      this.addTaskJiraWarning = null;
      if (!this.addTaskForm.taskItem.trim()) {
        this.addTaskSaveError = "Task Item is required.";
        return;
      }
      if (!this.addTaskForm.projectId || !this.addTaskForm.subProjectId) {
        this.addTaskSaveError = "Please select Project and Sub-Project.";
        return;
      }
      if (this.addTaskSaving) return;
      this.addTaskSaving = true;
      try {
        const payload = {
          projectPlanId: this.resolvedProjectPlanId || null,
          subProjectId: Number(this.addTaskForm.subProjectId),
          testPlanName: this.addTaskForm.taskItem.trim(),
          taskType: this.addTaskForm.taskType,
          taskLink: null,
          planOverallStart: this.addTaskForm.planOverallStart || null,
          planOverallDue: this.addTaskForm.planOverallDue || null,
          actionStartDate: null,
          actionDueDate: null,
          userIds: this.user?.userId ? [Number(this.user.userId)] : [],
          status: this.addTaskForm.status,
          details: this.addTaskForm.details || null,
          taskLink: this.serializeTaskJiraLinks(this.addTaskLinkedJira),
          createJiraSubtask: false,
          jiraIssueKeys: [],
          appBaseUrl: window.location.origin,
          actorUserId: this.user?.userId || null,
          fromMyTask: true,
        };
        const result = await testPlanItemsApi.insert(payload);
        if (result.jiraSubtasks && result.jiraSubtasks.length > 0) {
          const failed = result.jiraSubtasks.filter((r) => r.error);
          if (failed.length > 0) {
            this.addTaskJiraWarning = failed
              .map((f) => {
                const errDetail = f.error?.errors ? Object.values(f.error.errors).join(", ") : JSON.stringify(f.error);
                return `${f.parent}: ${errDetail}`;
              })
              .join("\n");
            await this.loadTasks();
            return;
          }
        }
        this.closeAddTaskModal();
        await this.loadTasks();
      } catch (e) {
        this.addTaskSaveError = e.message || "Save failed.";
      } finally {
        this.addTaskSaving = false;
      }
    },
    async loadTasks() {
      const userId = Number(this.user?.userId);
      if (!Number.isFinite(userId) || userId <= 0) {
        this.loading = false;
        return;
      }
      const seq = ++this._loadTasksSeq;
      const showSpinner = !this.tasks.length && !this.tasksLoadError;
      if (showSpinner) this.loading = true;
      this.tasksLoadError = null;
      try {
        const data = await myTasksApi.getByUser(userId);
        if (seq !== this._loadTasksSeq) return;
        if (!Array.isArray(data)) {
          throw new Error("Invalid response from server");
        }
        // Ensure all run tasks have a valid statusBreakdown; hide Deployed (process complete)
        this.tasks = data
          .map(t => t.taskType === 'run'
            ? { ...t, statusBreakdown: t.statusBreakdown || { pass: 0, passCond: 0, fail: 0, blocked: 0, notRun: 0 } }
            : t
          )
          .filter((t) => !this.isDeployedTaskStatus(t.status));
      } catch (e) {
        if (seq !== this._loadTasksSeq) return;
        const msg = e?.message || String(e);
        console.error("loadTasks error:", msg);
        this.tasks = [];
        if (/fetch|network|proxy|ECONNREFUSED|Failed to fetch/i.test(msg)) {
          this.tasksLoadError = "Cannot reach API server. Start the backend (yarn dev) on port 3000.";
        } else if (/initializing/i.test(msg)) {
          this.tasksLoadError = "Database is still initializing. Wait a few seconds and retry.";
        } else {
          this.tasksLoadError = msg;
        }
      } finally {
        if (seq === this._loadTasksSeq) this.loading = false;
      }
    },
    taskCardKey(task) {
      return `${task.taskType}-${task.id}`;
    },
    isJiraExpanded(task) {
      return !!this.expandedJiraKeys[this.taskCardKey(task)];
    },
    toggleJiraExpand(task) {
      const key = this.taskCardKey(task);
      this.expandedJiraKeys = {
        ...this.expandedJiraKeys,
        [key]: !this.expandedJiraKeys[key],
      };
    },
    formatDate(val) {
      if (!val) return "—";
      const [y, m, d] = val.split("-");
      return `${d}/${m}/${y}`;
    },
    parseCalendarDate(dueDate) {
      if (!dueDate) return null;
      const s = String(dueDate).slice(0, 10);
      const parts = s.split("-").map((x) => Number(x));
      if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
      const [y, m, d] = parts;
      const dt = new Date(y, m - 1, d);
      if (Number.isNaN(dt.getTime())) return null;
      dt.setHours(0, 0, 0, 0);
      return dt;
    },
    todayCalendar() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },
    isOverdue(dueDate) {
      const due = this.parseCalendarDate(dueDate);
      if (!due) return false;
      return due < this.todayCalendar();
    },
    isDueSoon(dueDate) {
      if (!dueDate || this.isOverdue(dueDate)) return false;
      const due = this.parseCalendarDate(dueDate);
      if (!due) return false;
      const diff = due.getTime() - this.todayCalendar().getTime();
      return diff <= 3 * 24 * 60 * 60 * 1000;
    },
    async openTaskDetail(task) {
      this.showTaskDetailModal = true;
      this.detailLoading = true;
      this.detailError = null;
      this.detailTask = { ...task };
      this.detailScopeJiraLinks = [];
      this.detailTaskJiraLinks = [];
      this.detailJiraLinks = [];
      this.jiraCreateError = null;
      this.detailForm = {
        name: task.name || "",
        planOverallStart: "",
        planOverallDue: task.dueDate || "",
        status: task.status || "to_do",
        details: "",
      };
      try {
        if (task.taskType === "plan") {
          const item = await testPlanItemsApi.getById(task.id);
          this.detailForm.name = item.testPlanName || task.name || "";
          this.detailForm.planOverallStart = item.planOverallStart ? String(item.planOverallStart).substring(0, 10) : "";
          this.detailForm.planOverallDue = item.planOverallDue ? String(item.planOverallDue).substring(0, 10) : (task.dueDate || "");
          this.detailForm.status = item.testPlanStatus || task.status || "to_do";
          this.detailForm.details = item.details || "";
          const rawTaskLink = item.taskLink || item.task_link || null;
          this.detailTaskJiraLinks = this.parseTaskJiraLinksRaw(rawTaskLink);
          await Promise.all([
            this.enrichDetailTaskJiraLinks(),
            this.loadDetailScopeJiraLinks(task.projectPlanId),
          ]);
        }
        if (task.taskType === "run") {
          const run = await testRunsApi.getById(task.id);
          const planId = run.testPlanId ?? run.test_plan_id ?? task.projectPlanId;
          this.detailTask = {
            ...this.detailTask,
            projectPlanId: planId,
            runId: run.id ?? task.id,
          };
          this.detailForm.name = run.testRunName || run.name || task.name || "";
          this.detailForm.planOverallStart = run.startDate ? String(run.startDate).substring(0, 10) : "";
          this.detailForm.planOverallDue = run.dueDate ? String(run.dueDate).substring(0, 10) : (task.dueDate || "");
          this.detailForm.status = run.status || task.status || "to_do";
          this.detailForm.details = run.testRunDescription || run.description || "";
        }
      } catch (e) {
        this.detailError = e.message || "Failed to load detail";
      } finally {
        this.detailLoading = false;
      }
    },
    async loadDetailScopeJiraLinks(projectPlanId) {
      this.detailScopeJiraLinks = [];
      this.detailJiraLinks = [];
      if (!projectPlanId) return;
      try {
        const pr = await fetchWithTimeout(`${API_BASE_URL}/api/project-plan/${projectPlanId}`);
        if (!pr.ok) return;
        const row = await pr.json();
        const raw = row.projectLink ? String(row.projectLink).trim() : null;
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        this.detailScopeJiraLinks = parsed;
        await this.enrichDetailJiraLinks();
      } catch (_) { /* ignore */ }
    },
    async enrichDetailJiraLinks() {
      if (!this.detailScopeJiraLinks.length) return;
      try {
        const data = await fetchJiraIssuesByKeys(this.detailScopeJiraLinks.map((l) => l.key));
        this.detailScopeJiraLinks = enrichJiraLinkRows(this.detailScopeJiraLinks, data);
        this.detailJiraLinks = [...this.detailScopeJiraLinks];
      } catch (_) { /* ignore */ }
    },
    async enrichDetailTaskJiraLinks() {
      if (!this.detailTaskJiraLinks.length) return;
      this.detailTaskJiraLoading = true;
      try {
        const data = await fetchJiraIssuesByKeys(this.detailTaskJiraLinks.map((l) => l.key));
        this.detailTaskJiraLinks = enrichJiraLinkRows(this.detailTaskJiraLinks, data);
      } catch (_) { /* ignore */ }
      finally {
        this.detailTaskJiraLoading = false;
      }
    },
    async enrichAddTaskLinkedJira() {
      if (!this.addTaskLinkedJira.length) return;
      this.addTaskLinkedJiraLoading = true;
      try {
        const data = await fetchJiraIssuesByKeys(this.addTaskLinkedJira.map((l) => l.key));
        this.addTaskLinkedJira = enrichJiraLinkRows(this.addTaskLinkedJira, data);
      } catch (_) { /* ignore */ }
      finally {
        this.addTaskLinkedJiraLoading = false;
      }
    },
    closeTaskDetailModal() {
      this.showTaskDetailModal = false;
      this.detailTask = null;
      this.detailScopeJiraLinks = [];
      this.detailTaskJiraLinks = [];
      this.detailJiraLinks = [];
      this.detailError = null;
      this.detailDeleting = false;
      this.jiraCreateError = null;
    },
    isMyTaskDeletable(task) {
      return task?.taskType === "plan" && !!task?.createdFromMyTask;
    },
    parseTaskJiraLinksRaw(raw) {
      return parseJiraLinks(raw).map((l) => {
        const key = String(l.key || "").trim().toUpperCase();
        return {
          key,
          url: l.url || `${this.jiraBrowseBase()}/${key}`,
          summary: l.summary || "",
          status: l.status || "",
        };
      }).filter((l) => l.key);
    },
    jiraBrowseBase() {
      const url = this.detailScopeJiraLinks[0]?.url
        || this.subProjectJiraLinks[0]?.url
        || this.addTaskLinkedJira[0]?.url
        || "";
      const m = String(url).match(/^(https?:\/\/[^/]+)\/browse/i);
      if (m) return `${m[1]}/browse`;
      return "https://lightnet-thailand.atlassian.net/browse";
    },
    serializeTaskJiraLinks(links) {
      const list = (Array.isArray(links) ? links : []).filter((l) => l?.key);
      if (!list.length) return null;
      const base = this.jiraBrowseBase();
      return JSON.stringify(
        list.map((l) => ({
          key: String(l.key).trim().toUpperCase(),
          url: l.url || `${base}/${l.key}`,
        }))
      );
    },
    scopeJiraProjectKeyFrom(links) {
      const k = (links || [])[0]?.key;
      return k ? String(k).split("-")[0] : "";
    },
    mergeTaskJiraLinks(target, keys) {
      const base = this.jiraBrowseBase();
      const byKey = new Map((target || []).map((l) => [String(l.key).toUpperCase(), l]));
      for (const key of keys) {
        const k = String(key || "").trim().toUpperCase();
        if (!k || byKey.has(k)) continue;
        byKey.set(k, { key: k, url: `${base}/${k}`, summary: "", status: "" });
      }
      return [...byKey.values()];
    },
    openJiraActionModal(mode, context) {
      this.jiraActionMode = mode;
      this.jiraActionContext = context;
      this.jiraActionError = null;
      this.jiraActionIssueKeys = [];
      this.jiraActionSearch = "";
      this.jiraIssues = [];
      this.jiraActionAssigneeAccountId = this.user?.jiraAccountId || "";
      const scopeLinks = context === "add" ? this.subProjectJiraLinks : this.detailScopeJiraLinks;
      this.jiraActionProjectKey = this.scopeJiraProjectKeyFrom(scopeLinks) || "";
      const defaultTitle = context === "add"
        ? this.addTaskForm.taskItem.trim()
        : (this.detailForm.name || "").trim();
      const defaultDetails = context === "add"
        ? (this.addTaskForm.details || "").trim()
        : (this.detailForm.details || "").trim();
      this.jiraActionTitle = defaultTitle;
      this.jiraActionDescription = defaultDetails || defaultTitle;
      this.showJiraActionModal = true;
      this.loadJiraProjectsForAddTask().then(() => {
        if (this.jiraActionProjectKey) {
          this.onJiraActionProjectChange();
        }
      });
    },
    closeJiraActionModal() {
      if (this.jiraCreatingIssue) return;
      this.showJiraActionModal = false;
      this.jiraActionMode = null;
      this.jiraActionContext = null;
      this.jiraActionIssueKeys = [];
      this.jiraActionError = null;
      this.jiraActionTitle = "";
      this.jiraActionDescription = "";
      this.jiraActionAssigneeAccountId = "";
      this.jiraAssignableUsers = [];
    },
    onJiraActionProjectChange() {
      this.jiraActionIssueKeys = [];
      this.jiraIssues = [];
      this.jiraActionSearch = "";
      if (this.jiraActionProjectKey) {
        this.loadJiraIssuesForAddTask(this.jiraActionProjectKey);
        this.loadJiraAssignableUsers();
      } else {
        this.jiraAssignableUsers = [];
      }
    },
    async loadJiraAssignableUsers() {
      const pk = this.jiraActionProjectKey;
      if (!pk) {
        this.jiraAssignableUsers = [];
        return;
      }
      this.jiraLoadingAssignable = true;
      try {
        const params = new URLSearchParams({ projectKey: pk });
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/assignable-users?${params.toString()}`, {
          headers: apiHeaders(),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(typeof data.error === "string" ? data.error : "Failed to load assignable users");
        }
        this.jiraAssignableUsers = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("loadJiraAssignableUsers:", e);
        this.jiraAssignableUsers = [];
      } finally {
        this.jiraLoadingAssignable = false;
      }
    },
    toggleJiraActionTicket(key) {
      const i = this.jiraActionIssueKeys.indexOf(key);
      if (this.jiraActionMode === "link") {
        if (i === -1) this.jiraActionIssueKeys.push(key);
        else this.jiraActionIssueKeys.splice(i, 1);
        return;
      }
      this.jiraActionIssueKeys = i === -1 ? [key] : [];
    },
    removeJiraActionTicket(key) {
      this.jiraActionIssueKeys = this.jiraActionIssueKeys.filter((k) => k !== key);
    },
    onJiraActionSearch() {
      clearTimeout(this.jiraActionSearchTimer);
      this.jiraActionSearchTimer = setTimeout(() => {
        if (this.jiraActionProjectKey) {
          this.loadJiraIssuesForAddTask(this.jiraActionProjectKey, this.jiraActionSearch);
        }
      }, 400);
    },
    async persistTaskJiraLinks(context, links) {
      const serialized = this.serializeTaskJiraLinks(links) || "[]";
      if (context === "detail" && this.detailTask?.id) {
        await testPlanItemsApi.update(this.detailTask.id, {
          taskLink: serialized,
          actorUserId: this.user?.userId || null,
        });
        this.detailTaskJiraLinks = [...links];
        const taskIdx = this.tasks.findIndex(
          (t) => t.taskType === "plan" && String(t.id) === String(this.detailTask.id)
        );
        if (taskIdx !== -1) {
          this.tasks[taskIdx] = { ...this.tasks[taskIdx], jiraLinks: [...links] };
        }
      }
    },
    async confirmJiraAction() {
      if (this.jiraActionConfirmDisabled) return;
      this.jiraActionError = null;
      const context = this.jiraActionContext;
      const mode = this.jiraActionMode;
      try {
        if (mode === "link") {
          const keys = [...this.jiraActionIssueKeys];
          if (context === "add") {
            this.addTaskLinkedJira = this.mergeTaskJiraLinks(this.addTaskLinkedJira, keys);
            await this.enrichAddTaskLinkedJira();
          } else {
            let merged = this.mergeTaskJiraLinks(this.detailTaskJiraLinks, keys);
            try {
              const data = await fetchJiraIssuesByKeys(merged.map((l) => l.key));
              merged = enrichJiraLinkRows(merged, data);
            } catch (_) { /* ignore */ }
            await this.persistTaskJiraLinks("detail", merged);
          }
          this.closeJiraActionModal();
          return;
        }
        const title = String(this.jiraActionTitle || "").trim();
        const description = String(this.jiraActionDescription || "").trim();
        const issueType = mode === "createBug" ? "bug" : "task";
        const linkedIssueKeys = [...this.jiraActionIssueKeys];
        this.jiraCreatingIssue = true;
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/create-issue`, {
          method: "POST",
          headers: apiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            title,
            description,
            projectKey: this.jiraActionProjectKey,
            standaloneMode: true,
            issueTypePreference: issueType,
            linkedIssueKeys,
            assigneeAccountId: this.jiraActionAssigneeAccountId || null,
            reporterAccountId: this.user?.jiraAccountId || null,
            appBaseUrl: typeof window !== "undefined" ? window.location.origin : null,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(typeof json.error === "string" ? json.error : "Failed to create Jira issue");
        const createdKey = json.key;
        if (!createdKey) throw new Error("Jira issue created but key missing");
        if (context === "add") {
          this.addTaskLinkedJira = this.mergeTaskJiraLinks(this.addTaskLinkedJira, [createdKey]);
          await this.enrichAddTaskLinkedJira();
        } else {
          let merged = this.mergeTaskJiraLinks(this.detailTaskJiraLinks, [createdKey]);
          try {
            const data = await fetchJiraIssuesByKeys([createdKey]);
            merged = enrichJiraLinkRows(merged, data);
          } catch (_) { /* ignore */ }
          await this.persistTaskJiraLinks("detail", merged);
        }
        this.closeJiraActionModal();
      } catch (e) {
        this.jiraActionError = e?.message || "Jira action failed";
      } finally {
        this.jiraCreatingIssue = false;
      }
    },
    removeAddTaskJiraLink(key) {
      const k = String(key || "").trim().toUpperCase();
      this.addTaskLinkedJira = this.addTaskLinkedJira.filter((l) => String(l.key).toUpperCase() !== k);
    },
    async removeDetailTaskJiraLink(key) {
      const k = String(key || "").trim().toUpperCase();
      const next = this.detailTaskJiraLinks.filter((l) => String(l.key).toUpperCase() !== k);
      try {
        await this.persistTaskJiraLinks("detail", next);
      } catch (e) {
        this.jiraCreateError = e?.message || "Failed to remove Jira link";
      }
    },
    requestDeleteMyTask(task) {
      if (!this.isMyTaskDeletable(task)) return;
      this.deleteConfirmTask = task;
      this.showDeleteConfirm = true;
    },
    cancelDeleteConfirm() {
      if (this.detailDeleting) return;
      this.showDeleteConfirm = false;
      this.deleteConfirmTask = null;
    },
    async confirmDeleteMyTask() {
      const task = this.deleteConfirmTask;
      if (!this.isMyTaskDeletable(task)) return;
      this.detailDeleting = true;
      this.detailError = null;
      try {
        await testPlanItemsApi.delete(task.id);
        this.showDeleteConfirm = false;
        this.deleteConfirmTask = null;
        if (this.showTaskDetailModal && this.detailTask?.id === task.id) {
          this.closeTaskDetailModal();
        }
        await this.loadTasks();
      } catch (e) {
        const msg = e?.message || "Delete failed.";
        if (this.showTaskDetailModal && this.detailTask?.id === task.id) {
          this.detailError = msg;
        } else {
          this.tasksLoadError = msg;
        }
      } finally {
        this.detailDeleting = false;
      }
    },
    async saveTaskDetail() {
      if (!this.detailTask || this.detailReadonly) return;
      this.detailSaving = true;
      this.detailError = null;
      try {
        await testPlanItemsApi.update(this.detailTask.id, {
          testPlanName: this.detailForm.name,
          planOverallStart: this.detailForm.planOverallStart || null,
          planOverallDue: this.detailForm.planOverallDue || null,
          actionStartDate: null,
          actionDueDate: null,
          userIds: this.user?.userId ? [Number(this.user.userId)] : [],
          status: this.detailForm.status,
          details: this.detailForm.details || null,
          taskLink: this.serializeTaskJiraLinks(this.detailTaskJiraLinks),
        });
        await this.loadTasks();
        this.closeTaskDetailModal();
      } catch (e) {
        this.detailError = e.message || "Save failed";
      } finally {
        this.detailSaving = false;
      }
    },
    goToPlanManagement() {
      const planId = this.detailTask?.projectPlanId;
      if (!planId) return;
      this.closeTaskDetailModal();
      this.$router.push({ name: "Test Plan Detail", params: { id: String(planId) } });
    },
    async goToTestRun() {
      const planId = this.detailTask?.projectPlanId ?? this.detailTask?.testPlanId;
      const runId = this.detailTask?.runId ?? this.detailTask?.id;
      if (!planId || !runId) {
        this.detailError = "Cannot open test run (missing plan link).";
        return;
      }
      this.closeTaskDetailModal();
      try {
        await this.$router.push({
          path: `/test-plan/${planId}/run/${runId}`,
        });
      } catch (err) {
        if (err?.name !== "NavigationDuplicated") {
          console.error("goToTestRun:", err);
        }
      }
    },
    onTaskCardClick(task) {
      if (task?.taskType === "jira" && task.jiraIssue) {
        this.openJiraIssueModalFromAll(task.jiraIssue);
        return;
      }
      this.openTaskDetail(task);
    },
    openJiraIssueModalFromAll(issue) {
      const sprint = this.jiraIssueSprint(issue);
      const sprintGroup = {
        sprintName: sprint?.name || "No Sprint",
        sprintState: sprint?.state || "",
      };
      const projectGroup = {
        projectKey: issue?.projectKey || "",
        projectName: issue?.projectName || issue?.projectKey || "",
      };
      this.openJiraIssueModal(issue, projectGroup, sprintGroup);
    },
    taskCardShowOverdue(task) {
      if (this.taskStatusExemptFromOverdue(task?.status)) return false;
      if (task?.taskType === "jira") {
        return !!(task.dueDate && this.isOverdue(task.dueDate));
      }
      return this.isOverdue(task?.dueDate);
    },
    isDeployedTaskStatus(status) {
      return normalizeSubProjectStatus(status) === "deployed";
    },
    isJiraCancelStatus(status) {
      const key = normalizeSubProjectStatus(status);
      return key === "cancel" || key === "cancelled" || key === "canceled";
    },
    isPassedTaskStatus(status) {
      const key = normalizeSubProjectStatus(status);
      return key === "passed" || key === "pass";
    },
    taskStatusExemptFromOverdue(status) {
      return (
        this.isDeployedTaskStatus(status) ||
        this.isPassedTaskStatus(status) ||
        this.isJiraDoneStatus(status)
      );
    },
    taskCardShowDueSoon(task) {
      if (this.taskStatusExemptFromOverdue(task?.status)) return false;
      if (task?.taskType === "jira") {
        return !!(task.dueDate && this.isDueSoon(task.dueDate));
      }
      return this.isDueSoon(task?.dueDate);
    },
    issueQualifiesForAllTab(issue) {
      if (this.isDeployedTaskStatus(issue?.status)) return false;
      if (this.isJiraCancelStatus(issue?.status)) return false;
      if (this.isJiraDoneStatus(issue?.status)) return false;
      return this.issueQualifiesActiveSprintOrDueWindow(issue);
    },
    /** Overdue or due Mon–Fri this week (open issues only). */
    issueHasUrgentDue(issue) {
      if (!issue?.dueDate || this.taskStatusExemptFromOverdue(issue?.status)) return false;
      return this.isOverdue(issue.dueDate) || this.isDueInCurrentWorkWeek(issue.dueDate);
    },
    /** Active sprint, or overdue / due Mon–Fri this week (excludes closed sprint otherwise). */
    issueQualifiesActiveSprintOrDueWindow(issue) {
      if (this.isDeployedTaskStatus(issue?.status)) return false;
      if (this.isJiraCancelStatus(issue?.status)) return false;
      if (this.isJiraDoneStatus(issue?.status)) return false;
      const sprint = this.jiraIssueSprint(issue);
      if (this.isJiraSprintClosed(sprint)) return this.issueHasUrgentDue(issue);
      if (this.isJiraSprintActive(sprint)) return true;
      return this.issueHasUrgentDue(issue);
    },
    isDueInCurrentWorkWeek(dueDate) {
      const due = this.parseCalendarDate(dueDate);
      if (!due) return false;
      const { mon, fri } = this.currentWorkWeekRange();
      return due >= mon && due <= fri;
    },
    currentWorkWeekRange() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const day = now.getDay();
      const monOffset = day === 0 ? -6 : 1 - day;
      const mon = new Date(now);
      mon.setDate(now.getDate() + monOffset);
      mon.setHours(0, 0, 0, 0);
      const fri = new Date(mon);
      fri.setDate(mon.getDate() + 4);
      fri.setHours(23, 59, 59, 999);
      return { mon, fri };
    },
    accentStyle(task) {
      if (task?.taskType === "jira") return this.jiraAccentStyle(task.jiraIssue || task);
      if (this.isOverdue(task.dueDate) && !this.taskStatusExemptFromOverdue(task.status)) return "background:#e74c3c;";
      if (task.taskType === "plan") return "background:#cb0c9f;";
      return "background:#1A73E8;";
    },
    jiraAccentStyle(issue) {
      if (
        issue?.dueDate &&
        this.isOverdue(issue.dueDate) &&
        !this.taskStatusExemptFromOverdue(issue.status)
      ) {
        return "background:#e74c3c;";
      }
      return "background:#0052CC;";
    },
    async openJiraIssueModal(issue, projectGroup, sprintGroup) {
      if (!issue) return;
      this.jiraModalError = null;
      this.selectedJiraIssue = {
        ...issue,
        transitions: issue.transitions || [],
        projectName: issue.projectName || projectGroup?.projectName || projectGroup?.projectKey || "",
        projectKey: issue.projectKey || projectGroup?.projectKey || "",
        sprintName: sprintGroup?.sprintName || "No Sprint",
        sprintState: sprintGroup?.sprintState || "",
      };
      this.jiraModalDueDate = issue.dueDate ? String(issue.dueDate).slice(0, 10) : "";
      this.showJiraIssueModal = true;
      try {
        const data = await fetchJiraTransitions(issue.key);
        this.onJiraModalStatusUpdate(data.status || issue.status || "");
        this.onJiraModalTransitionsUpdate(data.transitions || []);
      } catch (e) {
        this.jiraModalError = e.message || "Cannot load Jira transitions";
      }
    },
    closeJiraIssueModal() {
      this.showJiraIssueModal = false;
      this.selectedJiraIssue = null;
      this.jiraModalDueDate = "";
      this.jiraModalError = null;
      this.linkedTaskStatusSavingId = null;
    },
    syncJiraIssueInList(key, patch) {
      const k = String(key || "").trim().toUpperCase();
      if (!k) return;
      const apply = (list) =>
        (Array.isArray(list) ? list : []).map((row) =>
          String(row.key || "").toUpperCase() === k ? { ...row, ...patch } : row
        );
      this.jiraAssignedIssues = apply(this.jiraAssignedIssues);
      this.jiraReporterIssues = apply(this.jiraReporterIssues);
      if (this.selectedJiraIssue && String(this.selectedJiraIssue.key || "").toUpperCase() === k) {
        this.selectedJiraIssue = { ...this.selectedJiraIssue, ...patch };
      }
    },
    jiraIssueSprint(issue) {
      return issue?.nearestSprint || issue?.sprint || null;
    },
    isJiraSprintClosed(sprint) {
      return String(sprint?.state || "").trim().toLowerCase() === "closed";
    },
    isJiraSprintActive(sprint) {
      return String(sprint?.state || "").trim().toLowerCase() === "active";
    },
    issueMatchesJiraSubTabSprint(issue, tab) {
      if (this.isDeployedTaskStatus(issue?.status)) return false;
      if (this.isJiraCancelStatus(issue?.status)) return false;
      if (this.isJiraDoneStatus(issue?.status)) return false;
      const sprint = this.jiraIssueSprint(issue);
      const t = String(tab || this.jiraSubTab || "assigned").trim();
      if (t === "assigned" || t === "reporter") {
        return this.issueQualifiesActiveSprintOrDueWindow(issue);
      }
      if (t === "future") {
        if (this.isJiraSprintClosed(sprint)) return false;
        if (this.isJiraSprintActive(sprint)) return false;
        if (this.issueHasUrgentDue(issue)) return false;
        return true;
      }
      return true;
    },
    onJiraModalStatusUpdate(status) {
      if (!this.selectedJiraIssue) return;
      this.selectedJiraIssue.status = status || "";
      this.syncJiraIssueInList(this.selectedJiraIssue.key, { status: status || "" });
    },
    onJiraModalTransitionsUpdate(transitions) {
      if (!this.selectedJiraIssue) return;
      this.selectedJiraIssue.transitions = Array.isArray(transitions) ? transitions : [];
    },
    onJiraModalFieldError(message) {
      this.jiraModalError = message || "Jira update failed";
    },
    async saveJiraModalDueDate() {
      if (!this.selectedJiraIssue?.key) return;
      this.jiraModalSavingDue = true;
      this.jiraModalError = null;
      try {
        const dueDate = this.jiraModalDueDate ? String(this.jiraModalDueDate).slice(0, 10) : null;
        const data = await updateJiraDueDate(this.selectedJiraIssue.key, dueDate);
        const nextDue = data.dueDate || null;
        this.jiraModalDueDate = nextDue ? String(nextDue).slice(0, 10) : "";
        this.syncJiraIssueInList(this.selectedJiraIssue.key, { dueDate: nextDue });
        if (data.status) this.onJiraModalStatusUpdate(data.status);
      } catch (e) {
        this.jiraModalError = e.message || "Failed to update due date";
      } finally {
        this.jiraModalSavingDue = false;
      }
    },
    patchLinkedTaskInModal(taskId, patch) {
      if (!this.selectedJiraIssue?.linkedPlanTasks || !taskId) return;
      this.selectedJiraIssue.linkedPlanTasks = this.selectedJiraIssue.linkedPlanTasks.map((link) =>
        Number(link.taskId) === Number(taskId) ? { ...link, ...patch } : link
      );
    },
    async updateLinkedTaskStatus(link, newStatus) {
      if (!link?.taskId || newStatus == null || newStatus === "") return;
      const status = String(newStatus).trim();
      this.linkedTaskStatusSavingId = link.taskId;
      this.jiraModalError = null;
      try {
        await testPlanItemsApi.update(link.taskId, {
          status,
          actorUserId: this.user?.userId,
        });
        this.patchLinkedTaskInModal(link.taskId, { taskStatus: status });
      } catch (e) {
        this.jiraModalError = e.message || "Failed to update task status";
      } finally {
        this.linkedTaskStatusSavingId = null;
      }
    },
    normalizeTaskStatusValue(raw) {
      const s = String(raw || "").trim().toLowerCase().replace(/\s+/g, "_");
      if (!s) return "to_do";
      const match = this.taskStatusOptions.find((o) => o.value === s);
      return match ? match.value : s;
    },
    jiraLinkPageLabel(link) {
      if (!link) return "—";
      if (link.linkSource === "scope") return "Scope";
      if (link.linkSource === "task") return "Task Link";
      return "Plan";
    },
    jiraLinkedCount(issue) {
      const links = issue?.linkedPlanTasks;
      if (!Array.isArray(links) || links.length === 0) return 0;
      const seen = new Set();
      let n = 0;
      for (const link of links) {
        const id = this.jiraLinkRowKey(link);
        if (seen.has(id)) continue;
        seen.add(id);
        n += 1;
      }
      return n;
    },
    jiraLinkedCardTitle(issue) {
      const n = this.jiraLinkedCount(issue);
      if (!n) return "";
      return `${n} item(s) linked in TestOps — open card for details`;
    },
    openJiraIssueExternal() {
      const url = this.selectedJiraIssue?.url ? String(this.selectedJiraIssue.url).trim() : "";
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    jiraLinkedLabel(link) {
      if (!link) return "—";
      if (link.linkKind === "test_plan") {
        return link.projectPlanName || link.taskName || "Test Plan";
      }
      const plan = link.projectPlanName ? `${link.projectPlanName} / ` : "";
      return `${plan}${link.taskName || "Task"}`;
    },
    jiraLinkRowKey(link) {
      if (!link) return "";
      return [
        link.linkSource || "",
        link.linkKind || "",
        link.taskId || "",
        link.projectPlanId || "",
        link.taskName || "",
      ].join(":");
    },
    jiraIssueProjectSprintLabel(issue) {
      if (!issue) return "—";
      const project = issue.projectName || issue.projectKey || "—";
      const sprint = issue.sprintName || "No Sprint";
      const state = issue.sprintState ? ` (${issue.sprintState})` : "";
      return `${project} / ${sprint}${state}`;
    },
    jiraIssueStatusPillClass(status) {
      const s = String(status || "").trim().toLowerCase();
      if (s.includes("done")) return "pill-done";
      if (s.includes("progress")) return "pill-inprogress";
      if (s.includes("waiting") || s.includes("plan")) return "pill-waiting-planning";
      if (s.includes("to do") || s === "to_do") return "pill-todo";
      return "pill-draft";
    },
    isJiraDoneStatus(status) {
      const s = String(status || "").trim().toLowerCase();
      if (!s) return false;
      return s.includes("done") || s.includes("resolved") || s.includes("complete") || s.includes("closed");
    },
    statusLabel(task) {
      if (task.taskType === "jira") return task.status || "—";
      if (task.taskType === "plan" && isWorkflowStatusKey(task.status)) {
        return workflowStatusLabel(task.status);
      }
      if (task.taskType === "plan") {
        const s = String(task.status || "").replace(/_/g, " ");
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
      }
      return task.status || "—";
    },
    statusPillClass(task) {
      if (task.taskType === "jira") return this.jiraIssueStatusPillClass(task.status);
      if (task.taskType === "plan" && isWorkflowStatusKey(task.status)) {
        return workflowStatusPillClass(task.status);
      }
      if (task.taskType !== "plan") {
        const map = {
          "Not Started": "pill-not-started",
          "In Progress": "pill-run-inprogress",
          Done: "pill-run-done",
          Failed: "pill-run-failed",
        };
        return map[task.status] || "pill-draft";
      }
      return workflowStatusPillClass(task.status);
    },
    formatPlanType(planType) {
      const raw = String(planType || "plan").trim();
      if (!raw) return "plan";
      return raw.replace(/_/g, " ");
    },
    planTypeIcon(planType) {
      const key = String(planType || "plan").toLowerCase();
      const map = {
        plan: "fas fa-calendar-check",
        support: "fas fa-cog",
        other: "fas fa-tasks",
      };
      return map[key] || "fas fa-tag";
    },
  },
};
</script>

<style scoped>
/* Stat cards */
.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.mytask-delete-btn {
  color: #e74c3c;
  line-height: 1;
}

.mytask-delete-btn:hover {
  color: #c0392b;
}

.mytask-page-title {
  color: var(--text-primary);
}
.mytask-add-btn {
  font-size: 0.75rem !important;
}
.stat-col {
  flex: 1 1 180px;
}
.stat-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: var(--shadow-card);
}
.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  flex-shrink: 0;
}
.stat-icon-total { background: linear-gradient(195deg, #42424a, #191919); }
.stat-icon-plan { background: linear-gradient(195deg, #49a3f1, #1a73e8); }
.stat-icon-task { background: linear-gradient(195deg, #7c4dff, #5e35b1); }
.stat-icon-run { background: linear-gradient(195deg, #66bb6a, #43a047); }
.stat-icon-overdue { background: linear-gradient(195deg, #ef5350, #e53935); }
.stat-icon-jira { background: linear-gradient(195deg, #0052CC, #1D4E89); }
.stat-label { font-size: 0.72rem; color: var(--text-secondary); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.1rem; }
.stat-value { font-size: 1.4rem; font-weight: 700; color: var(--text-primary); margin: 0; }

/* Filter tabs */
.mytask-filter-tabs {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem;
  border-radius: 12px;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  box-shadow: 0 1px 4px rgba(52, 71, 103, 0.08);
}

.mytask-filter-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-primary);
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.45rem 0.85rem;
  line-height: 1.2;
}

.mytask-filter-item.active {
  background: var(--primary-light);
  color: var(--primary);
  box-shadow: none;
}

/* Task list */
.task-list { display: flex; flex-direction: column; gap: 0.75rem; }

.task-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  display: flex;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
}
.task-card:hover { box-shadow: 0 6px 24px rgba(0,0,0,0.13); transform: translateY(-1px); }
.task-overdue { border: 1px solid #fde8e8; }

.task-accent { width: 4px; flex-shrink: 0; }
.task-body { padding: 1rem 1.2rem; flex: 1; min-width: 0; }

.task-name { font-size: 0.9rem; font-weight: 700; color: var(--text-primary); }
.task-project { font-size: 0.75rem; }
.task-project-icon { font-size: 0.65rem; }

.task-due { font-size: 0.72rem; color: var(--text-secondary); white-space: nowrap; flex-shrink: 0; }
.due-soon { color: #e67e22 !important; font-weight: 600; }
.due-overdue { color: #e74c3c !important; font-weight: 600; }

/* Jira tab — Plan Management shell */
.mytask-jira-shell-card {
  border-radius: 0.75rem;
}
.mytask-jira-shell-card .card-header {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}
.mytask-jira-shell-content {
  padding-top: 0.85rem;
}
.mytask-jira-shell-card .tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}
.mytask-jira-shell-card .tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}
.mytask-jira-shell-card .tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}
.mytask-jira-shell-card .tab-item--future {
  font-size: 0.62rem;
  letter-spacing: 0.01em;
}
.mytask-jira-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid var(--surface-border);
}
.mytask-jira-project-filter {
  min-width: 200px;
  max-width: 280px;
  flex: 0 1 280px;
}
.mytask-jira-project-filter :deep(.base-select-trigger) {
  min-height: 2.05rem;
  font-size: 0.78rem;
}
.mytask-jira-project-filter :deep(.base-select-value) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mytask-jira-toolbar-meta {
  flex: 1;
  min-width: 200px;
  text-align: right;
}
.mytask-jira-connected {
  line-height: 1.35;
  padding-bottom: 0.15rem;
}
.mytask-jira-tab-hint {
  line-height: 1.35;
  margin-top: 0.2rem;
}
.mytask-jira-stats .stat-card {
  padding: 0.65rem 0.75rem;
}
.mytask-jira-stats .stat-icon {
  width: 34px;
  height: 34px;
  font-size: 0.82rem;
}
.mytask-jira-stats .stat-value {
  font-size: 1.1rem;
}
.stat-icon-linked {
  background: linear-gradient(195deg, #cb0c9f, #9a0a7a);
}
.mytask-jira-list {
  gap: 0.5rem;
}
.mytask-jira-project-head {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: #344767;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.65rem 0.25rem 0.35rem;
  margin-top: 0.15rem;
  border-bottom: 1px solid #e9ecef;
}
.mytask-jira-project-head:first-child {
  margin-top: 0;
  padding-top: 0.15rem;
}
.mytask-jira-sprint-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.72rem;
  font-weight: 600;
  color: #67748e;
  padding: 0.45rem 0.25rem 0.35rem 0.15rem;
  cursor: pointer;
  user-select: none;
  border-radius: 8px;
  transition: background 0.15s ease;
}
.mytask-jira-sprint-head:hover {
  background: #f6f8fb;
}
.mytask-jira-sprint-chevron {
  color: #17c1e8;
  font-size: 0.65rem;
}
.mytask-jira-sprint-state {
  margin-left: 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #8392ab;
}
.mytask-jira-sprint-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.15rem 0 0.35rem 1.1rem;
}
.mytask-jira-meta {
  color: #7b809a;
}
.badge-jira {
  background: #e3f0ff;
  color: #0052cc;
  border: 1px solid #b3d4fc;
}
.linked-testops-pill {
  background: #fce4f3;
  color: #cb0c9f;
  border: 1px solid #f0b8dd;
}
.mytask-modal-pane-side {
  width: 580px;
  flex-shrink: 0;
  overflow: hidden;
  max-height: 65vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid #e9ecef;
}
.mytask-side-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1rem;
  min-height: 120px;
}
.mytask-side-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
}
.mytask-side-table th {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8392ab;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #fafbfc;
  position: sticky;
  top: 0;
  z-index: 1;
}
.mytask-side-table td {
  padding: 0.45rem 0.5rem;
  vertical-align: middle;
  border-color: #f0f2f5;
  font-size: 0.75rem;
}
.mytask-side-name {
  font-weight: 600;
  color: var(--text-primary);
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mytask-side-sub {
  max-width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mytask-side-page {
  white-space: nowrap;
  font-weight: 600;
}
.mytask-side-status {
  min-width: 9.5rem;
  max-width: 11rem;
}
.mytask-side-status :deep(.base-select-trigger) {
  min-height: 1.65rem;
  font-size: 0.72rem;
}
.mytask-modal-pane-form :deep(.jira-status-select-wrap) {
  max-width: 100%;
}

/* Type badges */
.task-type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.15rem 0.55rem;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.badge-plan { background: #f4eeff; color: #7c4dff; border: 1px solid #d5b8ff; }
.badge-run  { background: #e8f4fd; color: #1A73E8; border: 1px solid #bee5eb; }
.badge-subtask { background: #efe8ff; color: #5e35b1; border: 1px solid #d1bfff; }

.overdue-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.15rem 0.55rem;
  border-radius: 50px;
  background: #fde8e8;
  color: #e74c3c;
  border: 1px solid #f9c0c0;
}

/* Run status breakdown pills */
.run-stat {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 50px;
}
.run-stat-pass     { background: #d4f5e2; color: #28a745; border: 1px solid #b7efd0; }
.run-stat-passcond { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
.run-stat-fail     { background: #fde8e8; color: #e74c3c; border: 1px solid #f9c0c0; }
.run-stat-blocked  { background: #fde8d8; color: #c0510d; border: 1px solid #f9c8a0; }
.run-stat-notrun   { background: #e9ecef; color: #6c757d; border: 1px solid #dee2e6; }
.run-stat-complete { background: #d4f5e2; color: #28a745; border: 1px solid #b7efd0; }
.progress-value { color: var(--text-primary); }
.task-progress-track {
  height: 6px;
  border-radius: 3px;
  background: #e9ecef;
  overflow: hidden;
  margin-bottom: 0.6rem;
}
.task-progress-bar {
  background: #cb0c9f;
}
.task-progress-complete {
  background: #82d616;
}

/* Add Task modal (aligned with Test Plan tab modal) */
.add-task-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.add-task-modal-box {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  width: 480px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}
.add-task-modal-box--split {
  width: 1100px;
  max-width: 98vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.mytask-modal-split-body {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.mytask-modal-pane-form {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  max-height: 65vh;
  border-right: 1px solid #e9ecef;
}
.mytask-modal-pane-jira {
  width: 520px;
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 65vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.task-jira-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fafbfc;
}
.task-jira-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.45rem 0.65rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 0.75rem;
  color: var(--text-primary);
}
.task-jira-toggle:hover {
  background: #f0f4f8;
}
.task-jira-toggle-start {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}
.task-jira-toggle-label {
  font-weight: 700;
  white-space: nowrap;
}
.task-jira-toggle-end {
  margin-left: auto;
  flex-shrink: 0;
  padding-left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: #8392ab;
  font-size: 0.65rem;
}
.task-jira-panel {
  padding: 0.5rem 0.65rem 0.65rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
.task-jira-panel :deep(.jira-panel-table-wrap) {
  max-height: 220px;
}
.qa-badge-mini {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
}
.jira-issue-list-mini {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item-mini {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}
.jira-issue-item-mini:last-child {
  border-bottom: none;
}
.jira-issue-selected-mini {
  background: #e8f4fd;
}
.jira-create-box {
  background: #f0f9ff;
  border: 1px solid #bee5eb;
}
.mytask-jira-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.35rem;
}
.mytask-jira-actions .btn {
  font-size: 0.62rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.mytask-jira-table-wrap {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #fff;
}
.mytask-jira-table {
  table-layout: fixed;
  width: 100%;
}
.mytask-jira-col-id {
  width: 72px;
}
.mytask-jira-col-status {
  width: 108px;
}
.mytask-jira-col-action {
  width: 44px;
}
.mytask-jira-col-title {
  width: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mytask-jira-col-status :deep(.jira-status-select--mini) {
  width: 100%;
  max-width: 100%;
}
.mytask-jira-table th {
  font-size: 0.62rem;
  text-transform: uppercase;
  color: #8392ab;
  padding: 0.35rem 0.4rem;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.mytask-jira-table td {
  padding: 0.35rem 0.4rem;
  vertical-align: middle;
  font-size: 0.72rem;
  border-color: #f0f2f5;
}
.action-delete {
  color: #e74c3c;
  line-height: 1;
}
.action-delete:hover {
  color: #c0392b;
}
.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}
.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}
.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.jira-issue-item:last-child {
  border-bottom: none;
}
.jira-issue-item:hover {
  background: #f8f9fa;
}
.jira-issue-selected {
  background: #e8f4fd;
}
.add-task-details-textarea {
  font-size: 0.8rem;
  resize: vertical;
}
.nowrap-btn {
  white-space: nowrap;
}
</style>
